import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    Autocomplete,
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Divider,
    Chip,
    Checkbox,
    FormGroup,
    InputAdornment,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    useMediaQuery,
    Theme,
    Radio,
    RadioGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BlobProvider, PDFDownloadLink } from '@react-pdf/renderer';
import {
    useDataProvider,
    useNotify,
    useGetIdentity,
    useRedirect,
    useGetList,
    Loading,
} from 'react-admin';
import { Link, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { Account, Contact, Quote, QuoteLine } from '../types';

// Import the PDF document component
import { RatePdfDocument } from './RatePdfDocument';
import { PdfPreviewRenderer } from './PdfPreviewRenderer';
// Import types from the new types file
import {
    LocationCropEntry,
    RateLookupFormData,
    RateData,
    ProcessedRateData,
    StateCropClassData,
    CoverageData,
    DistinctCarrierData,
    UniqueCropOption,
    EndorsementDefinitionData,
    EndorsementRateData,
    EndorsementOption,
} from './types';
import { findEndorsementRate } from './endorsementRates';
import { selectCarrierRate } from './baseRates';
// Import configuration context
import { useConfigurationContext } from '../root/ConfigurationContext';
import { supabase } from '../providers/supabase/supabase';

// Define static data arrays (needed for form inputs)
import { stateAbbreviationMap } from '../utils/stateAbbreviations';

type AccountOrCreate = Account | { __create: true; name: string };

type DiscountLookup = {
    aip: string;
    state: string;
    has_discount: boolean;
    discount: number | null;
    discount_deadline: string | null;
    excluded_crops: string[];
    excluded_products: string[];
};

// Helper function to normalize case (e.g., "CASCADE" -> "Cascade", "BIG HORN" -> "Big Horn")
const normalizeCase = (str: string | null | undefined): string => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

// Helper function to format rate values for display (rates stored as XX.X = percentage)
const formatRateValue = (rate: string | number | null | undefined): string => {
    if (rate === null || rate === undefined || rate === '') return '-';

    let numRate: number;
    if (typeof rate === 'string') {
        const cleanedRate = rate.replace(/[^\d.-]/g, '');
        numRate = parseFloat(cleanedRate);
    } else {
        numRate = rate;
    }

    return isNaN(numRate) ? '-' : numRate.toFixed(2) + '%';
};

// Parse rate to number for calculations (rate in DB is XX.X = percentage)
const getRateAsNumber = (rate: string | number | null | undefined): number => {
    if (rate === null || rate === undefined || rate === '') return 0;
    const s = String(rate).replace(/[^\d.-]/g, '');
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
};

// Helper function to format crop display text
const formatCropDisplay = (crop: string, cropClass: string): string => {
    if (crop === 'Potatoes' && (cropClass === 'H' || cropClass === 'P')) {
        return 'Potatoes (H/P)';
    }
    return `${crop} (${cropClass})`;
};

// Format IPA for table title: IPA $XX.XX
const formatIpaForTitle = (ipa: number | undefined): string =>
    `IPA $${Number(ipa ?? 1).toFixed(2)}`;

const states = ['Montana', 'Idaho', 'Washington', 'Oregon', 'Wyoming'];
// const allCarriers = ['RCIS', 'NAU', 'Rain & Hail', 'AgriSompo'];
// const allHailProducts = ... // Removed as it's dynamic now
// const paymentMethods = ... // Keep if needed elsewhere

// Helper map for display carrier name -> tag prefix (with correct casing)
const carrierNameToTagPrefix: { [key: string]: string } = {
    RCIS: 'RCIS',
    NAU: 'NAU',
    'Rain & Hail': 'rainHail',
    AgriSompo: 'agriSompo', // This one seems lowercase in the example
    'Great American': 'greatAmerican',
    proAg: 'proAg',
    'Pro Ag': 'proAg',
    ProAg: 'proAg',
};

/**
 * HailPricingToolPage Component
 *
 * Redesigned as a page for quickly entering parameters and fetching/viewing rates.
 */
export const HailPricingToolPage: React.FC = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const theme = useTheme();
    const { quoteStages, quoteCategories } = useConfigurationContext();
    const isSmallScreen = useMediaQuery((themePARAM: Theme) =>
        themePARAM.breakpoints.down('sm')
    );
    const {
        identity,
        isLoading: identityLoading,
        error: identityError,
    } = useGetIdentity();
    const { logo: cropWestLogoUrl } = useConfigurationContext(); // Get the global logo

    // Location & Crop lines (first line = main; user can add more)
    const [locationCropLines, setLocationCropLines] = useState<
        LocationCropEntry[]
    >(() => [
        {
            id: `line-${Date.now()}`,
            state: '',
            county: '',
            township: '',
            range: '',
            crop: '',
            ipa: 1,
        },
    ]);
    // Options caches for additional lines (keyed by state abbr or state_county etc.)
    const [countyCache, setCountyCache] = useState<Record<string, string[]>>(
        {}
    );
    const [townshipCache, setTownshipCache] = useState<
        Record<string, string[]>
    >({});
    const [rangeCache, setRangeCache] = useState<Record<string, string[]>>({});
    const [cropCache, setCropCache] = useState<
        Record<string, UniqueCropOption[]>
    >({});

    // State for the form inputs (shared: carriers, products; location comes from locationCropLines)
    const [formData, setFormData] = useState<Partial<RateLookupFormData>>({
        insuredName: '',
        state: '',
        county: '',
        township: '',
        range: '',
        crop: '',
        ipa: 1,
        class: '',
        availableClasses: [],
        selectedCarriers: [],
        selectedHailProducts: [],
    });

    // State for fetched rates and loading/error status
    const [processedRates, setProcessedRates] = useState<ProcessedRateData[]>(
        []
    );
    // When multiple lines: rates per line. When single line (legacy): processedRates still set for first line.
    const [ratesByLine, setRatesByLine] = useState<
        Array<{
            lineIndex: number;
            label: string;
            entry: LocationCropEntry | null;
            rates: ProcessedRateData[];
        }>
    >([]);
    const [isLoadingRates, setIsLoadingRates] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // Cash discount state
    const [applyCashDiscount, setApplyCashDiscount] = useState(false);
    const [discountData, setDiscountData] = useState<DiscountLookup[]>([]);

    // Add state for county options and loading
    const [countyOptions, setCountyOptions] = useState<string[]>([]);
    const [loadingCounties, setLoadingCounties] = useState(false);

    // Add state for township options and loading
    const [townshipOptions, setTownshipOptions] = useState<string[]>([]);
    const [loadingTownships, setLoadingTownships] = useState(false);

    // Add state for range options and loading
    const [rangeOptions, setRangeOptions] = useState<string[]>([]);
    const [loadingRanges, setLoadingRanges] = useState(false);

    // State for crop options (now holds unique crop objects with associated classes)
    const [cropOptions, setCropOptions] = useState<UniqueCropOption[]>([]);
    const [loadingCrops, setLoadingCrops] = useState(false);

    // State to track if a fetch has been attempted since last input change
    const [fetchAttempted, setFetchAttempted] = useState(false);

    // State for ALL fetched coverage data
    const [allCoveragesData, setAllCoveragesData] = useState<CoverageData[]>(
        []
    );
    const [loadingAllCoverages, setLoadingAllCoverages] = useState(true); // Start loading on mount

    // State for dynamically filtered coverage options (for UI)
    const [coverageOptions, setCoverageOptions] = useState<string[]>([]);

    // State for available carrier options
    const [carrierOptions, setCarrierOptions] = useState<string[]>([]);
    const [loadingCarriers, setLoadingCarriers] = useState(true); // Start loading

    // State for hail product options that are actually available based on fetched rates
    const [
        availableHailProductOptionsForDisplay,
        setAvailableHailProductOptionsForDisplay,
    ] = useState<string[]>([]);

    // Endorsement state
    const [endorsementDefs, setEndorsementDefs] = useState<
        EndorsementDefinitionData[]
    >([]);
    const [endorsementRates, setEndorsementRates] = useState<
        EndorsementRateData[]
    >([]);
    const [selectedEndorsements, setSelectedEndorsements] = useState<
        Map<string, string>
    >(new Map());

    // --- State for PDF Preview Modal ---
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

    // --- State for Save as Quote dialog ---
    const [saveQuoteDialogOpen, setSaveQuoteDialogOpen] = useState(false);
    const [saveQuoteAccountId, setSaveQuoteAccountId] = useState<number | null>(
        null
    );
    const [saveQuoteAccountName, setSaveQuoteAccountName] = useState('');
    const [saveQuoteContactId, setSaveQuoteContactId] = useState<number | null>(
        null
    );
    const [saveQuoteContactName, setSaveQuoteContactName] = useState('');
    const [saveQuoteSaving, setSaveQuoteSaving] = useState(false);

    // Form-level account (above Quote Name): select existing or create new
    const [selectedFormAccount, setSelectedFormAccount] =
        useState<Account | null>(null);
    const [formAccountInputValue, setFormAccountInputValue] = useState('');
    const [extraAccounts, setExtraAccounts] = useState<Account[]>([]);
    const [extraContacts, setExtraContacts] = useState<Contact[]>([]);
    const [createAccountDialogOpen, setCreateAccountDialogOpen] =
        useState(false);
    const [createAccountFromSaveQuote, setCreateAccountFromSaveQuote] =
        useState(false);
    const [createAccountName, setCreateAccountName] = useState('');
    const [createAccountSaving, setCreateAccountSaving] = useState(false);
    // --- State for Create Contact (from Save as Quote) dialog ---
    const [createContactDialogOpen, setCreateContactDialogOpen] =
        useState(false);
    const [createContactFirstName, setCreateContactFirstName] = useState('');
    const [createContactLastName, setCreateContactLastName] = useState('');
    const [createContactSaving, setCreateContactSaving] = useState(false);

    // Accounts and contacts for Save as Quote dialog (user's accounts via RLS)
    const { data: accountOptions = [] } = useGetList<Account>('accounts', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'name', order: 'ASC' },
    });
    const { data: contactOptions = [] } = useGetList<Contact>(
        'contacts_summary',
        {
            filter: saveQuoteAccountId
                ? { account_id: saveQuoteAccountId }
                : { account_id: -1 },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'last_name', order: 'ASC' },
        }
    );

    // State for indicating table content is being updated/filtered
    const [isUpdatingDisplay, setIsUpdatingDisplay] = useState(false);

    // IPA inputs: use string while editing so cursor doesn't jump and decimals work (e.g. "1.")
    const [ipaFieldFocused, setIpaFieldFocused] = useState(false);
    const [ipaInputStr, setIpaInputStr] = useState('');
    const [editingIpaLineIndex, setEditingIpaLineIndex] = useState<
        number | null
    >(null);
    const [editingIpaStr, setEditingIpaStr] = useState('');

    // --- Edit mode state (from ?quoteId=xxx URL param) ---
    const [searchParams] = useSearchParams();
    const editQuoteId = searchParams.get('quoteId');
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingQuoteRecord, setEditingQuoteRecord] = useState<Quote | null>(
        null
    );
    const [hydratingFromQuote, setHydratingFromQuote] = useState(!!editQuoteId);
    const hydratingRef = useRef(false);

    // States for managing minimum display time of initial product loading skeleton
    const [
        minimumLoadingTimeMetForInitialFetch,
        setMinimumLoadingTimeMetForInitialFetch,
    ] = useState(false);
    const [
        actualFetchCompletedForInitialFetch,
        setActualFetchCompletedForInitialFetch,
    ] = useState(false);

    // --- Input Handlers (copied/adapted from QuoteCreate) ---
    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        const isIpa = name === 'ipa';
        const nextValue = isIpa
            ? (() => {
                  const n = parseFloat(value);
                  return isNaN(n) || n < 0 ? 1 : n;
              })()
            : type === 'checkbox' || type === 'switch'
              ? checked
              : value;
        setFormData(prevData => ({
            ...prevData,
            [name]: nextValue,
        }));
        if (isIpa) {
            const numVal = typeof nextValue === 'number' ? nextValue : 1;
            setLocationCropLines(prev => {
                const next = [...prev];
                if (next[0]) next[0] = { ...next[0], ipa: numVal };
                return next;
            });
        }
        setFetchAttempted(false);
    };

    const handleSelectChange = (e: SelectChangeEvent<string | string[]>) => {
        const { name, value } = e.target;
        setFetchAttempted(false);

        // Special handling for crop selection to set crop and available classes
        if (name === 'crop') {
            const selectedCropName = value as string;
            const selectedOption = cropOptions.find(
                opt => opt.crop === selectedCropName
            );
            setFormData(prevData => ({
                ...prevData,
                crop: selectedOption ? selectedOption.crop : '',
                availableClasses: selectedOption ? selectedOption.classes : [],
                class: '', // Reset class since we're selecting a new crop
            }));
            setLocationCropLines(prev => {
                const next = [...prev];
                if (next[0])
                    next[0] = {
                        ...next[0],
                        crop: selectedOption ? selectedOption.crop : '',
                    };
                return next;
            });
            return;
        }

        // Generic select handling & cascading resets
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
            ...(name === 'state' && {
                county: '',
                township: '',
                range: '',
                crop: '',
                class: '',
                availableClasses: [],
                selectedHailProducts: [],
            }),
            ...(name === 'county' && { township: '', range: '' }),
            ...(name === 'township' && { range: '' }),
        }));
        // Keep first location/crop line in sync for multi-line fetch
        if (['state', 'county', 'township', 'range', 'crop'].includes(name)) {
            setLocationCropLines(prev => {
                const next = [...prev];
                if (next[0])
                    next[0] = {
                        ...next[0],
                        [name]: value as string,
                        ...(name === 'state' && {
                            county: '',
                            township: '',
                            range: '',
                            crop: '',
                        }),
                        ...(name === 'county' && { township: '', range: '' }),
                        ...(name === 'township' && { range: '' }),
                    };
                return next;
            });
        }
    };

    const addLocationCropLine = useCallback(() => {
        setLocationCropLines(prev => {
            const last = prev[prev.length - 1];
            const defaults = last
                ? {
                      state: last.state,
                      county: last.county,
                      township: last.township,
                      range: last.range,
                      crop: last.crop,
                      ipa: last.ipa ?? 1,
                  }
                : {
                      state: '',
                      county: '',
                      township: '',
                      range: '',
                      crop: '',
                      ipa: 1,
                  };
            return [
                ...prev,
                {
                    id: `line-${Date.now()}-${prev.length}`,
                    ...defaults,
                },
            ];
        });
        setFetchAttempted(false);
    }, []);

    const removeLocationCropLine = useCallback((index: number) => {
        if (index <= 0) return;
        setLocationCropLines(prev => prev.filter((_, i) => i !== index));
        setRatesByLine(prev => prev.filter(r => r.lineIndex !== index));
        setFetchAttempted(false);
    }, []);

    const updateLocationCropLine = useCallback(
        (index: number, patch: Partial<LocationCropEntry>) => {
            if (index <= 0) return;
            setLocationCropLines(prev => {
                const next = [...prev];
                if (next[index])
                    next[index] = {
                        ...next[index],
                        ...patch,
                    } as LocationCropEntry;
                return next;
            });
            setFetchAttempted(false);
        },
        []
    );

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        listName: keyof Pick<
            RateLookupFormData,
            'selectedCarriers' | 'selectedHailProducts'
        >
    ) => {
        const { name: item, checked } = event.target;
        if (
            listName === 'selectedHailProducts' ||
            listName === 'selectedCarriers'
        ) {
            setIsUpdatingDisplay(true);
        }
        setFormData(prevData => {
            const currentList = prevData[listName] || [];
            let updatedList;
            if (checked) {
                updatedList = [...currentList, item];
            } else {
                updatedList = currentList.filter(i => i !== item);
            }
            // If carriers changed, reset selected products (options handled by effect)
            if (listName === 'selectedCarriers') {
                return {
                    ...prevData,
                    [listName]: updatedList,
                    selectedHailProducts: [],
                };
            }
            return { ...prevData, [listName]: updatedList };
        });
        // Only reset fetchAttempted if not modifying hail products directly
        if (listName !== 'selectedHailProducts') {
            setFetchAttempted(false);
        }
    };

    const handleChipSelection = (
        item: string,
        listName: keyof Pick<
            RateLookupFormData,
            'selectedCarriers' | 'selectedHailProducts'
        >
    ) => {
        if (
            listName === 'selectedHailProducts' ||
            listName === 'selectedCarriers'
        ) {
            setIsUpdatingDisplay(true);
        }
        setFormData(prevData => {
            const currentList = prevData[listName] || [];
            const updatedList = currentList.filter(i => i !== item);
            // If carriers changed, reset selected products (options handled by effect)
            if (listName === 'selectedCarriers') {
                return {
                    ...prevData,
                    [listName]: updatedList,
                    selectedHailProducts: [],
                };
            }
            return { ...prevData, [listName]: updatedList };
        });
        // Only reset fetchAttempted if not modifying hail products directly
        if (listName !== 'selectedHailProducts') {
            setFetchAttempted(false);
        }
    };

    // --- Fetch Counties based on selected State ---
    useEffect(() => {
        const fetchCounties = async () => {
            const selectedStateFullName = formData.state;
            // Use the existing map from this file
            const stateAbbreviation = selectedStateFullName
                ? stateAbbreviationMap[selectedStateFullName]
                : undefined;

            if (!selectedStateFullName || !stateAbbreviation) {
                setCountyOptions([]);
                return; // No state selected or abbreviation not found, clear counties
            }

            setLoadingCounties(true);
            setCountyOptions([]); // Clear previous options

            try {
                // console.log(
                //     `Fetching counties for state: ${selectedStateFullName} (Abbr: ${stateAbbreviation})`
                // );
                const { data } = await dataProvider.getList(
                    'state_county_map',
                    {
                        pagination: { page: 1, perPage: 1000 }, // Fetch all counties for the state
                        sort: { field: 'county', order: 'ASC' },
                        filter: { state: stateAbbreviation },
                    }
                );

                if (data) {
                    // Process for case-insensitive uniqueness, preserving original case
                    const uniqueCountiesMap = new Map<string, string>();
                    data.forEach((item: { county: string }) => {
                        const lowerCaseCounty = item.county.toLowerCase();
                        if (!uniqueCountiesMap.has(lowerCaseCounty)) {
                            uniqueCountiesMap.set(
                                lowerCaseCounty,
                                normalizeCase(item.county)
                            ); // Store normalized case
                        }
                    });
                    const uniqueCountyNames = Array.from(
                        uniqueCountiesMap.values()
                    ).sort();
                    // console.log(
                    //     'Processed unique counties:',
                    //     uniqueCountyNames
                    // );
                    setCountyOptions(uniqueCountyNames);
                } else {
                    setCountyOptions([]);
                }
            } catch (error) {
                console.error(
                    `Error fetching counties for state ${selectedStateFullName}:`,
                    error
                );
                setCountyOptions([]); // Clear options on error
            } finally {
                setLoadingCounties(false);
            }
        };

        fetchCounties();
    }, [formData.state, dataProvider]); // Re-run when state changes

    // --- Fetch Townships based on selected State and County ---
    useEffect(() => {
        const fetchTownships = async () => {
            const selectedStateFullName = formData.state;
            const selectedCounty = formData.county;
            const stateAbbreviation = selectedStateFullName
                ? stateAbbreviationMap[selectedStateFullName]
                : undefined;

            if (!stateAbbreviation || !selectedCounty) {
                setTownshipOptions([]);
                return; // Need both state and county
            }

            setLoadingTownships(true);
            setTownshipOptions([]);

            try {
                // console.log(
                //     `Fetching townships for state: ${stateAbbreviation}, county: ${selectedCounty}`
                // );
                const { data } = await dataProvider.getList(
                    'state_county_township_map',
                    {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'township', order: 'ASC' },
                        filter: {
                            state: stateAbbreviation,
                            county: selectedCounty,
                        },
                    }
                );

                if (data) {
                    const townshipValues = data.map(
                        (item: { township: string }) => item.township
                    );
                    // console.log('Fetched townships:', townshipValues);
                    setTownshipOptions(townshipValues);
                } else {
                    setTownshipOptions([]);
                }
            } catch (error) {
                console.error(
                    `Error fetching townships for ${stateAbbreviation}, ${selectedCounty}:`,
                    error
                );
                setTownshipOptions([]);
            } finally {
                setLoadingTownships(false);
            }
        };

        fetchTownships();
    }, [formData.state, formData.county, dataProvider]); // Re-run when state or county changes

    // --- Fetch Ranges based on selected State, County, and Township ---
    useEffect(() => {
        const fetchRanges = async () => {
            const selectedStateFullName = formData.state;
            const selectedCounty = formData.county;
            const selectedTownship = formData.township; // Get selected township
            const stateAbbreviation = selectedStateFullName
                ? stateAbbreviationMap[selectedStateFullName]
                : undefined;

            // Check for state, county, AND township before fetching
            if (!stateAbbreviation || !selectedCounty || !selectedTownship) {
                setRangeOptions([]);
                return; // Need state, county, and township
            }

            setLoadingRanges(true);
            setRangeOptions([]);

            try {
                // console.log(
                //     `Fetching ranges for state: ${stateAbbreviation}, county: ${selectedCounty}, township: ${selectedTownship}`
                // );
                const { data } = await dataProvider.getList(
                    'state_county_range_map',
                    {
                        // Use the updated view name
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'range', order: 'ASC' },
                        // Add township to the filter
                        filter: {
                            state: stateAbbreviation,
                            county: selectedCounty,
                            township: selectedTownship,
                        },
                    }
                );

                if (data) {
                    const rangeValues = data.map(
                        (item: { range: string }) => item.range
                    );
                    // console.log('Fetched ranges:', rangeValues);
                    setRangeOptions(rangeValues);
                } else {
                    setRangeOptions([]);
                }
            } catch (error) {
                console.error(
                    `Error fetching ranges for ${stateAbbreviation}, ${selectedCounty}, ${selectedTownship}:`,
                    error
                );
                setRangeOptions([]);
            } finally {
                setLoadingRanges(false);
            }
        };

        fetchRanges();
        // Add formData.township to dependencies
    }, [formData.state, formData.county, formData.township, dataProvider]);

    // --- Fetch Crops/Classes based on selected State ---
    useEffect(() => {
        const fetchCrops = async () => {
            // Skip resets while hydrating from a quote to preserve saved values
            const isHydrating = hydratingRef.current;

            const selectedStateFullName = formData.state;
            const stateAbbreviation = selectedStateFullName
                ? stateAbbreviationMap[selectedStateFullName]
                : undefined;

            if (!stateAbbreviation) {
                setCropOptions([]);
                if (!isHydrating) {
                    // Reset crop and class in form data if state is cleared
                    setFormData(prev => ({
                        ...prev,
                        crop: '',
                        class: '',
                        availableClasses: [],
                    }));
                }
                return;
            }

            setLoadingCrops(true);
            setCropOptions([]);
            if (!isHydrating) {
                // Reset crop and class in form data when fetching new crops
                setFormData(prev => ({
                    ...prev,
                    crop: '',
                    class: '',
                    availableClasses: [],
                }));
            }
            try {
                // console.log(
                //     `Fetching crop/class combos for state: ${stateAbbreviation}`
                // );
                // Fetch the full objects from the database
                const { data } = await dataProvider.getList<StateCropClassData>(
                    'state_crop_map',
                    {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'crop', order: 'ASC' },
                        filter: { state: stateAbbreviation },
                    }
                );

                if (data) {
                    // console.log('Fetched crop/class combos:', data);

                    // Group crops by name, collect classes and per-carrier classes
                    const cropGroupsMap = new Map<
                        string,
                        {
                            classes: Set<string>;
                            carrierClasses: Map<string, Set<string>>;
                        }
                    >();

                    data.forEach(item => {
                        if (!cropGroupsMap.has(item.crop)) {
                            cropGroupsMap.set(item.crop, {
                                classes: new Set(),
                                carrierClasses: new Map(),
                            });
                        }
                        const group = cropGroupsMap.get(item.crop)!;
                        group.classes.add(item.class);
                        if (item.aip) {
                            if (!group.carrierClasses.has(item.aip)) {
                                group.carrierClasses.set(item.aip, new Set());
                            }
                            group.carrierClasses.get(item.aip)!.add(item.class);
                        }
                    });

                    // Convert to UniqueCropOption array
                    const uniqueCropOptions: UniqueCropOption[] = Array.from(
                        cropGroupsMap.entries()
                    ).map(([cropName, group]) => ({
                        crop: cropName,
                        classes: Array.from(group.classes).sort(),
                        state: stateAbbreviation,
                        carrierClasses: Array.from(
                            group.carrierClasses.entries()
                        )
                            .map(([aip, cls]) => ({
                                aip,
                                classes: Array.from(cls).sort(),
                            }))
                            .sort((a, b) => a.aip.localeCompare(b.aip)),
                    }));

                    // Sort by crop name
                    uniqueCropOptions.sort((a, b) =>
                        a.crop.localeCompare(b.crop)
                    );

                    // console.log(
                    //     'Processed unique crop options:',
                    //     uniqueCropOptions
                    // );
                    setCropOptions(uniqueCropOptions);
                } else {
                    setCropOptions([]);
                }
            } catch (error) {
                console.error(
                    `Error fetching crop/class combos for ${stateAbbreviation}:`,
                    error
                );
                setCropOptions([]);
            } finally {
                setLoadingCrops(false);
            }
        };

        fetchCrops();
    }, [formData.state, dataProvider]); // Re-run when state changes

    // --- Populate option caches for additional location/crop lines ---
    useEffect(() => {
        const extra = locationCropLines.slice(1);
        const stateAbbrs = [
            ...new Set(
                extra
                    .map(l => l.state && stateAbbreviationMap[l.state])
                    .filter(Boolean) as string[]
            ),
        ];
        stateAbbrs.forEach(async stateAbbr => {
            if (countyCache[stateAbbr] !== undefined) return;
            try {
                const { data } = await dataProvider.getList(
                    'state_county_map',
                    {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'county', order: 'ASC' },
                        filter: { state: stateAbbr },
                    }
                );
                if (data?.length) {
                    const map = new Map<string, string>();
                    data.forEach((item: { county: string }) => {
                        const k = item.county.toLowerCase();
                        if (!map.has(k)) map.set(k, normalizeCase(item.county));
                    });
                    setCountyCache(prev => ({
                        ...prev,
                        [stateAbbr]: Array.from(map.values()).sort(),
                    }));
                }
            } catch {
                setCountyCache(prev => ({ ...prev, [stateAbbr]: [] }));
            }
        });
    }, [locationCropLines, dataProvider]); // countyCache intentionally omitted to avoid re-fetch

    const cacheKey = (a: string, b: string, c?: string) =>
        c ? `${a}::${b}::${c}` : `${a}::${b}`;

    useEffect(() => {
        const extra = locationCropLines.slice(1);
        const keys = extra
            .filter(l => l.state && l.county)
            .map(l => cacheKey(stateAbbreviationMap[l.state!], l.county!));
        const unique = [...new Set(keys)];
        unique.forEach(async key => {
            if (townshipCache[key] !== undefined) return;
            const [stateAbbr, ...countyParts] = key.split('::');
            const county = countyParts.join('::');
            if (!stateAbbr || !county) return;
            try {
                const { data } = await dataProvider.getList(
                    'state_county_township_map',
                    {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'township', order: 'ASC' },
                        filter: { state: stateAbbr, county },
                    }
                );
                const list =
                    data?.map((item: { township: string }) => item.township) ??
                    [];
                setTownshipCache(prev => ({ ...prev, [key]: list }));
            } catch {
                setTownshipCache(prev => ({ ...prev, [key]: [] }));
            }
        });
    }, [locationCropLines, dataProvider]);

    useEffect(() => {
        const extra = locationCropLines.slice(1);
        const keys = extra
            .filter(l => l.state && l.county && l.township)
            .map(l =>
                cacheKey(stateAbbreviationMap[l.state!], l.county!, l.township!)
            );
        const unique = [...new Set(keys)];
        unique.forEach(async key => {
            if (rangeCache[key] !== undefined) return;
            const [stateAbbr, county, township] = key.split('::');
            if (!stateAbbr || !county || !township) return;
            try {
                const { data } = await dataProvider.getList(
                    'state_county_range_map',
                    {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'range', order: 'ASC' },
                        filter: { state: stateAbbr, county, township },
                    }
                );
                const list =
                    data?.map((item: { range: string }) => item.range) ?? [];
                setRangeCache(prev => ({ ...prev, [key]: list }));
            } catch {
                setRangeCache(prev => ({ ...prev, [key]: [] }));
            }
        });
    }, [locationCropLines, dataProvider]);

    useEffect(() => {
        const extra = locationCropLines.slice(1);
        const stateAbbrs = [
            ...new Set(
                extra
                    .map(l => l.state && stateAbbreviationMap[l.state])
                    .filter(Boolean) as string[]
            ),
        ];
        stateAbbrs.forEach(async stateAbbr => {
            if (cropCache[stateAbbr] !== undefined) return;
            try {
                const { data } = await dataProvider.getList<StateCropClassData>(
                    'state_crop_map',
                    {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'crop', order: 'ASC' },
                        filter: { state: stateAbbr },
                    }
                );
                if (data?.length) {
                    const cropGroupsMap = new Map<
                        string,
                        {
                            classes: Set<string>;
                            carrierClasses: Map<string, Set<string>>;
                        }
                    >();
                    data.forEach(item => {
                        if (!cropGroupsMap.has(item.crop))
                            cropGroupsMap.set(item.crop, {
                                classes: new Set(),
                                carrierClasses: new Map(),
                            });
                        const group = cropGroupsMap.get(item.crop)!;
                        group.classes.add(item.class);
                        if (item.aip) {
                            if (!group.carrierClasses.has(item.aip))
                                group.carrierClasses.set(item.aip, new Set());
                            group.carrierClasses.get(item.aip)!.add(item.class);
                        }
                    });
                    const options: UniqueCropOption[] = Array.from(
                        cropGroupsMap.entries()
                    ).map(([cropName, group]) => ({
                        crop: cropName,
                        classes: Array.from(group.classes).sort(),
                        state: stateAbbr,
                        carrierClasses: Array.from(
                            group.carrierClasses.entries()
                        )
                            .map(([aip, cls]) => ({
                                aip,
                                classes: Array.from(cls).sort(),
                            }))
                            .sort((a, b) => a.aip.localeCompare(b.aip)),
                    }));
                    options.sort((a, b) => a.crop.localeCompare(b.crop));
                    setCropCache(prev => ({ ...prev, [stateAbbr]: options }));
                } else {
                    setCropCache(prev => ({ ...prev, [stateAbbr]: [] }));
                }
            } catch {
                setCropCache(prev => ({ ...prev, [stateAbbr]: [] }));
            }
        });
    }, [locationCropLines, dataProvider]);

    // --- Fetch ALL Coverages Once on Mount ---
    useEffect(() => {
        const fetchAllCoverages = async () => {
            setLoadingAllCoverages(true);
            // console.log('Fetching all crop coverages...');
            try {
                // No filter applied here
                const { data } = await dataProvider.getList<CoverageData>(
                    'crop_coverages',
                    {
                        pagination: { page: 1, perPage: 1000 }, // Fetch all (adjust if more than 1000 expected)
                        sort: { field: 'coverage', order: 'ASC' },
                        filter: {},
                    }
                );
                if (data) {
                    // console.log(
                    //     `Fetched ${data.length} total coverage records. Example:`,
                    //     data[0]
                    // ); // Updated log
                    setAllCoveragesData(data);
                } else {
                    setAllCoveragesData([]);
                    notify('Could not fetch crop coverage data.', {
                        type: 'warning',
                    });
                }
            } catch (error) {
                console.error('Error fetching all coverages:', error);
                notify('Error fetching crop coverage data.', { type: 'error' });
                setAllCoveragesData([]);
            } finally {
                setLoadingAllCoverages(false);
            }
        };
        fetchAllCoverages();
        // Run only once on mount
    }, [dataProvider, notify]);

    // --- Fetch Endorsement Definitions Once on Mount ---
    useEffect(() => {
        const fetchEndorsementDefs = async () => {
            try {
                const { data } =
                    await dataProvider.getList<EndorsementDefinitionData>(
                        'endorsement_definitions',
                        {
                            pagination: { page: 1, perPage: 1000 },
                            sort: { field: 'endorsement', order: 'ASC' },
                            filter: {},
                        }
                    );
                if (data) setEndorsementDefs(data);
            } catch (error) {
                console.error('Error fetching endorsement definitions:', error);
            }
        };
        fetchEndorsementDefs();
    }, [dataProvider]);

    // --- Fetch Distinct Carriers Once on Mount ---
    useEffect(() => {
        const fetchCarriers = async () => {
            setLoadingCarriers(true);
            // console.log('Fetching distinct carriers...');
            try {
                const { data } =
                    await dataProvider.getList<DistinctCarrierData>(
                        'distinct_carriers',
                        {
                            pagination: { page: 1, perPage: 100 }, // Fetch reasonable limit
                            sort: { field: 'carrier', order: 'ASC' },
                            filter: {},
                        }
                    );
                if (data) {
                    const carrierNames = data.map(item => item.carrier);
                    // console.log(
                    //     `Fetched ${carrierNames.length} distinct carriers:`,
                    //     carrierNames
                    // );
                    setCarrierOptions(carrierNames);
                } else {
                    setCarrierOptions([]);
                    notify('Could not fetch carrier list.', {
                        type: 'warning',
                    });
                }
            } catch (error) {
                console.error('Error fetching carriers:', error);
                notify('Error fetching carrier list.', { type: 'error' });
                setCarrierOptions([]);
            } finally {
                setLoadingCarriers(false);
            }
        };
        fetchCarriers();
    }, [dataProvider, notify]);

    // --- Filter Coverages based on State and Selected Carriers (Frontend) ---
    useEffect(() => {
        // Skip resets while hydrating from a quote to preserve saved values
        if (hydratingRef.current) return;

        const stateAbbr = formData.state
            ? stateAbbreviationMap[formData.state]
            : null;
        const carriers = formData.selectedCarriers || [];

        // Only filter if base data is loaded and we have selections
        if (
            loadingAllCoverages ||
            !stateAbbr ||
            carriers.length === 0 ||
            allCoveragesData.length === 0
        ) {
            setCoverageOptions([]);
            // Clear selected products if dependencies change/are missing
            // Check if selected products exist before clearing to avoid unnecessary redraws
            if (
                formData.selectedHailProducts &&
                formData.selectedHailProducts.length > 0
            ) {
                setFormData(prev => ({ ...prev, selectedHailProducts: [] }));
            }
            return;
        }

        // Generate target tags
        const targetTags = carriers
            .map(carrierName => carrierNameToTagPrefix[carrierName])
            .filter(prefix => !!prefix)
            .map(prefix => `${prefix}-${stateAbbr}`);

        if (targetTags.length === 0) {
            setCoverageOptions([]); // No valid tags generated
            return;
        }

        // Filter the fetched data
        const filteredCoverages = allCoveragesData.filter(
            coverageItem =>
                // Check if the item's tags array has at least one tag from targetTags
                coverageItem.tags &&
                coverageItem.tags.some(tag => targetTags.includes(tag))
        );

        // Get unique coverage names from the filtered list
        const uniqueCoverages = [
            ...new Set(filteredCoverages.map(item => item.coverage)),
        ];
        setCoverageOptions(uniqueCoverages.sort());
        // Reset selected products when options change (important!)
        setFormData(prev => ({ ...prev, selectedHailProducts: [] }));

        // Depend on the form selections AND the fetched data
    }, [
        formData.state,
        formData.selectedCarriers,
        allCoveragesData,
        loadingAllCoverages,
    ]);

    // --- Effect to update available hail product options based on fetched rates ---
    useEffect(() => {
        if (processedRates.length > 0 && !isLoadingRates) {
            const selectedCarriers = formData.selectedCarriers || [];

            // Only include products that have rates for at least one selected carrier
            const productsWithRatesForSelectedCarriers = processedRates
                .filter(rate => {
                    const hasCarrier =
                        typeof rate.carrier === 'string' &&
                        selectedCarriers.includes(rate.carrier);
                    const hasForm = typeof rate.displayForm === 'string';
                    const hasValidRate =
                        rate.selected_rate !== null &&
                        rate.selected_rate !== undefined &&
                        rate.selected_rate !== '' &&
                        rate.selected_rate !== '-';

                    return hasCarrier && hasForm && hasValidRate;
                })
                .map(rate => rate.displayForm)
                .filter((form): form is string => !!form);

            const uniqueProductsFromRates = [
                ...new Set(productsWithRatesForSelectedCarriers),
            ].sort();

            setAvailableHailProductOptionsForDisplay(uniqueProductsFromRates);

            // Filter current selections to only include products that are still available
            setFormData(prev => {
                const currentSelected = prev.selectedHailProducts || [];
                const newSelected = currentSelected.filter(product =>
                    uniqueProductsFromRates.includes(product)
                );
                if (currentSelected.length !== newSelected.length) {
                    return { ...prev, selectedHailProducts: newSelected };
                }
                return prev;
            });
        } else if (!isLoadingRates) {
            // If no rates or loading is finished, clear the options and selections
            setAvailableHailProductOptionsForDisplay([]);
            // Only clear if there were selections, to avoid unnecessary updates
            if (
                formData.selectedHailProducts &&
                formData.selectedHailProducts.length > 0
            ) {
                setFormData(prev => ({ ...prev, selectedHailProducts: [] }));
            }
        }
    }, [processedRates, isLoadingRates, formData.selectedCarriers]);

    // --- Handler for the 'Select All' Carriers checkbox ---
    const handleSelectAllCarriersChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isChecked = event.target.checked;
        setFormData(prevData => ({
            ...prevData,
            selectedCarriers: isChecked ? [...carrierOptions] : [],
        }));
    };

    // --- Handler for the 'Select All' Hail Products checkbox ---
    const handleSelectAllProductsChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isChecked = event.target.checked;
        setIsUpdatingDisplay(true); // This always affects the display
        setFormData(prevData => ({
            ...prevData,
            // Set to all available options if checked, otherwise clear
            selectedHailProducts: isChecked
                ? [...availableHailProductOptionsForDisplay]
                : [],
        }));
        // setFetchAttempted(false); // No longer needed here, as this action doesn't invalidate fetched rates
    };

    // --- Discount Lookup ---
    const fetchDiscountData = useCallback(
        async (stateAbbr: string): Promise<DiscountLookup[]> => {
            if (!stateAbbr) return [];
            try {
                const { data, error } = await supabase
                    .from('discount_manager')
                    .select(
                        'aip, state, has_discount, discount, discount_deadline, excluded_crops, excluded_products'
                    )
                    .eq('state', stateAbbr);
                if (error) {
                    console.error('Error fetching discounts:', error);
                    return [];
                }
                return (data as DiscountLookup[]) ?? [];
            } catch (err) {
                console.error('Error fetching discounts:', err);
                return [];
            }
        },
        []
    );

    // --- Rate Fetching Logic (Updated to: Two-query approach with crop_classes lookup) ---
    const handleFetchRates = async () => {
        console.log('[FETCH-TOP] handleFetchRates called');
        // Reset states for managing minimum loading display time
        setMinimumLoadingTimeMetForInitialFetch(false);
        setActualFetchCompletedForInitialFetch(false);

        setIsLoadingRates(true);
        setFetchError(null);
        setProcessedRates([]);
        setRatesByLine([]);
        setFormData(prev => ({ ...prev, selectedHailProducts: [] }));
        setFetchAttempted(true);

        setTimeout(() => {
            setMinimumLoadingTimeMetForInitialFetch(true);
        }, 500);

        const selectedCarriers = formData.selectedCarriers || [];
        if (!selectedCarriers.length) {
            notify('Please select at least one Carrier.', { type: 'warning' });
            setIsLoadingRates(false);
            return;
        }

        // Build list of location/crop lines to fetch (line 0 = formData, rest = locationCropLines)
        const firstLine = locationCropLines[0];
        const linesToFetch: Array<{
            lineIndex: number;
            label: string;
            entry: LocationCropEntry | null;
            stateAbbr: string;
            county: string;
            township: string;
            range: string;
            crop: string;
        }> = [];
        const firstStateAbbr = firstLine?.state
            ? stateAbbreviationMap[firstLine.state]
            : undefined;
        if (
            firstStateAbbr &&
            firstLine?.county &&
            firstLine?.township &&
            firstLine?.range &&
            firstLine?.crop
        ) {
            linesToFetch.push({
                lineIndex: 0,
                label: `${firstLine.county} – ${firstLine.crop} – ${firstLine.township} – ${firstLine.range} - ${formatIpaForTitle(firstLine.ipa)}`,
                entry: firstLine,
                stateAbbr: firstStateAbbr,
                county: firstLine.county,
                township: firstLine.township,
                range: firstLine.range,
                crop: firstLine.crop,
            });
        }
        locationCropLines.slice(1).forEach((entry, i) => {
            const stateAbbr = entry.state
                ? stateAbbreviationMap[entry.state]
                : undefined;
            if (
                stateAbbr &&
                entry.county &&
                entry.township &&
                entry.range &&
                entry.crop
            ) {
                linesToFetch.push({
                    lineIndex: i + 1,
                    label: `${entry.county} – ${entry.crop} – ${entry.township} – ${entry.range} - ${formatIpaForTitle(entry.ipa)}`,
                    entry,
                    stateAbbr,
                    county: entry.county,
                    township: entry.township,
                    range: entry.range,
                    crop: entry.crop,
                });
            }
        });

        if (linesToFetch.length === 0) {
            notify(
                'Please fill in State, County, Township, Range, and Crop for at least one location.',
                {
                    type: 'warning',
                }
            );
            setIsLoadingRates(false);
            return;
        }

        // Fetch discount data for the state
        const stateAbbrForDiscount = linesToFetch[0]?.stateAbbr;
        if (stateAbbrForDiscount) {
            const discounts = await fetchDiscountData(stateAbbrForDiscount);
            setDiscountData(discounts);
        } else {
            setDiscountData([]);
        }

        // Maps any AIP / carrier name variant to the canonical
        // carrier name used in the `rates` table.  The rates table
        // is the source of truth for carrier names because
        // `distinct_carriers` (which populates the checkbox list
        // and `selectedCarriers`) is derived from it.
        const aipToCarrierMap: { [key: string]: string } = {
            AgriSompo: 'AgriSompo',
            RCIS: 'RCIS',
            NAU: 'NAU',
            proAg: 'Pro Ag',
            ProAg: 'Pro Ag',
            'Pro Ag': 'Pro Ag',
            'Rain & Hail': 'Rain & Hail',
            rainHail: 'Rain & Hail',
            'Great American': 'Great American',
            greatAmerican: 'Great American',
            Chubb: 'Chubb',
            Hudson: 'Hudson',
        };

        try {
            const resultsByLine: Array<{
                lineIndex: number;
                label: string;
                entry: LocationCropEntry | null;
                rates: ProcessedRateData[];
            }> = [];
            let firstLineAvailableClasses: string[] = [];

            for (const line of linesToFetch) {
                const {
                    stateAbbr,
                    county,
                    township,
                    range,
                    crop: selectedCropName,
                } = line;

                const { data: allCropClassData } = await dataProvider.getList<{
                    id: string;
                    state: string;
                    crop: string;
                    class: string;
                    aip: string;
                }>('crop_classes', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'class', order: 'ASC' },
                    filter: { state: stateAbbr, crop: selectedCropName },
                });

                const cropClassData =
                    allCropClassData?.filter(record => {
                        const canonicalName =
                            aipToCarrierMap[record.aip] || record.aip;
                        return (
                            selectedCarriers.includes(canonicalName) ||
                            selectedCarriers.includes(record.aip)
                        );
                    }) || [];

                const carrierClassMap = new Map<string, string[]>();
                if (cropClassData.length > 0) {
                    cropClassData.forEach(record => {
                        const carrierName =
                            aipToCarrierMap[record.aip] || record.aip;
                        if (!carrierClassMap.has(carrierName)) {
                            carrierClassMap.set(carrierName, []);
                        }
                        carrierClassMap.get(carrierName)!.push(record.class);
                    });
                    if (line.lineIndex === 0) {
                        firstLineAvailableClasses = [
                            ...new Set(cropClassData.map(r => r.class)),
                        ].sort();
                    }
                }

                const ratesFilter = {
                    state: stateAbbr,
                    township,
                    range,
                };
                const { data: allRatesData } =
                    await dataProvider.getList<RateData>('rates', {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'carrier', order: 'ASC' },
                        filter: ratesFilter,
                    });

                const ratesData =
                    allRatesData?.filter(
                        record =>
                            record.carrier &&
                            selectedCarriers.includes(record.carrier)
                    ) || [];

                if (ratesData.length === 0) {
                    resultsByLine.push({
                        lineIndex: line.lineIndex,
                        label: line.label,
                        entry: line.entry,
                        rates: [],
                    });
                    continue;
                }

                const lowerCaseCounty = county.toLowerCase();
                const countyFilteredData = ratesData.filter(
                    record =>
                        record.county &&
                        record.county.toLowerCase() === lowerCaseCounty
                );

                if (countyFilteredData.length === 0) {
                    resultsByLine.push({
                        lineIndex: line.lineIndex,
                        label: line.label,
                        entry: line.entry,
                        rates: [],
                    });
                    continue;
                }

                // Resolve each row's rate strictly through the carrier's
                // crop_classes entry. Rows whose carrier has no entry for
                // this crop are dropped — that carrier does not quote it.
                // The earlier fallback would scan every class column for
                // the crop and surface whichever held a number, which
                // caused unrelated carriers to inherit another carrier's
                // class rate (e.g. Great American picking up Rain & Hail's
                // B-class rate on Garbanzo Beans).
                const transformedData: ProcessedRateData[] = countyFilteredData
                    .map(record => {
                        const selectedRate = selectCarrierRate(
                            record,
                            carrierClassMap
                        );
                        if (selectedRate === null) return null;

                        let displayForm = record.form;
                        const originalForm = record.form;
                        let aliasMatchFound = false;
                        if (originalForm && allCoveragesData.length > 0) {
                            const formTrimmed = originalForm.trim();
                            const formLower = formTrimmed.toLowerCase();
                            // Normalize: strip dashes, extra spaces, and
                            // punctuation so "Basic - 5% Min w/ Schedule A"
                            // matches alias "Basic 5% MIN w/ Schedule A"
                            const normalize = (s: string) =>
                                s
                                    .toLowerCase()
                                    .replace(/[^a-z0-9%/]/g, ' ')
                                    .replace(/\s+/g, ' ')
                                    .trim();
                            const formNorm = normalize(formTrimmed);
                            // First check if form matches a coverage_alias
                            const matchingCoverage = allCoveragesData.find(
                                coverage =>
                                    coverage.coverage_alias &&
                                    Array.isArray(coverage.coverage_alias) &&
                                    coverage.coverage_alias.some(
                                        alias =>
                                            alias.trim().toLowerCase() ===
                                                formLower ||
                                            normalize(alias) === formNorm
                                    )
                            );
                            if (matchingCoverage) {
                                displayForm = matchingCoverage.coverage;
                                aliasMatchFound = true;
                            } else {
                                // Also check if form matches a main coverage name
                                const exactCoverage = allCoveragesData.find(
                                    coverage =>
                                        coverage.coverage
                                            .trim()
                                            .toLowerCase() === formLower ||
                                        normalize(coverage.coverage) ===
                                            formNorm
                                );
                                if (exactCoverage) {
                                    displayForm = exactCoverage.coverage;
                                }
                            }
                        }
                        return {
                            ...record,
                            displayForm,
                            aliasMatchFound,
                            selected_rate: selectedRate,
                        } as ProcessedRateData;
                    })
                    .filter((r): r is ProcessedRateData => r !== null);

                resultsByLine.push({
                    lineIndex: line.lineIndex,
                    label: line.label,
                    entry: line.entry,
                    rates: transformedData,
                });
            }

            setRatesByLine(resultsByLine);
            setProcessedRates(resultsByLine.flatMap(r => r.rates));
            setFormData(prev => ({
                ...prev,
                availableClasses: firstLineAvailableClasses,
            }));

            // Fetch endorsement rates in its own try/catch.
            //
            // Cache rows for every (state, carrier) referenced by any line —
            // do NOT pre-filter by crop or county. The per-line lookup
            // (`findEndorsementRate`) handles those dimensions, so additional
            // locations with different crops/counties resolve correctly.
            console.log('[ENDO] Starting endorsement fetch');
            try {
                const uniqueStates = Array.from(
                    new Set(
                        linesToFetch
                            .map(l => l.stateAbbr)
                            .filter((s): s is string => !!s)
                    )
                );
                console.log(
                    '[ENDO] params:',
                    JSON.stringify({ uniqueStates, selectedCarriers })
                );
                let allEndoRates: EndorsementRateData[] = [];
                for (const state of uniqueStates) {
                    for (const carrier of selectedCarriers) {
                        try {
                            const { data: carrierRates } =
                                await dataProvider.getList<EndorsementRateData>(
                                    'endorsement_rates',
                                    {
                                        filter: {
                                            state,
                                            carrier,
                                        },
                                        pagination: {
                                            page: 1,
                                            perPage: 5000,
                                        },
                                        sort: {
                                            field: 'id',
                                            order: 'ASC',
                                        },
                                    }
                                );
                            if (carrierRates) {
                                allEndoRates =
                                    allEndoRates.concat(carrierRates);
                            }
                        } catch (carrierErr) {
                            console.error(
                                `[ENDO] Error fetching ${state}/${carrier}:`,
                                carrierErr
                            );
                        }
                    }
                }
                console.log(
                    '[ENDO] total fetched across (state, carrier):',
                    allEndoRates.length
                );
                setEndorsementRates(allEndoRates);
            } catch (endoErr) {
                console.error('[ENDO] Error:', endoErr);
                setEndorsementRates([]);
            }
        } catch (error: any) {
            console.error('❌ Error in two-step rate fetching:', error);
            setFetchError(
                `Error fetching rates: ${error.message || 'Unknown error'}`
            );
            notify(
                `Error fetching rates: ${error.message || 'Unknown error'}`,
                { type: 'error' }
            );
            setProcessedRates([]);
            setRatesByLine([]);
        } finally {
            setActualFetchCompletedForInitialFetch(true);
        }
    };

    // Effect to manage isLoadingRates based on actual fetch completion and minimum display time
    useEffect(() => {
        if (
            minimumLoadingTimeMetForInitialFetch &&
            actualFetchCompletedForInitialFetch
        ) {
            setIsLoadingRates(false);
        }
    }, [
        minimumLoadingTimeMetForInitialFetch,
        actualFetchCompletedForInitialFetch,
    ]);

    const handleReset = () => {
        setProcessedRates([]);
        setRatesByLine([]);
        setDiscountData([]);
        setFormData(prev => ({
            ...prev,
            selectedHailProducts: [],
            // Consider if other fields like insuredName should be reset here too
            // For a full form reset, you might re-initialize to the initial formData state
        }));
        setFetchAttempted(false);
        setFetchError(null);
        // The button text/functionality will change back to "Fetch Rates" due to conditional rendering
        // Also reset the minimum loading time flags for the next fetch cycle
        setMinimumLoadingTimeMetForInitialFetch(false);
        setActualFetchCompletedForInitialFetch(false);
    };

    // --- Endorsement helpers ---
    const availableEndorsements = useMemo(() => {
        const grouped = new Map<
            string,
            { endorsement: string; rates: Map<string, number> }[]
        >();

        const currentState = locationCropLines[0]?.state
            ? stateAbbreviationMap[locationCropLines[0].state]
            : formData.state
              ? stateAbbreviationMap[formData.state]
              : '';
        const targetTags = (formData.selectedCarriers || []).map(
            c => `${carrierNameToTagPrefix[c] || c}-${currentState}`
        );

        console.log('[Endorsements] targetTags:', targetTags);
        console.log(
            '[Endorsements] all defs:',
            endorsementDefs.map(d => ({
                e: d.endorsement,
                tags: d.tags,
            }))
        );
        console.log(
            '[Endorsements] endorsementRates count:',
            endorsementRates.length
        );

        const relevantDefs = endorsementDefs.filter(d =>
            d.tags.some(t => targetTags.includes(t))
        );
        console.log(
            '[Endorsements] relevantDefs:',
            relevantDefs.map(d => d.endorsement)
        );

        if (endorsementRates.length > 0) {
            console.log('[Endorsements] rate endorsement names:', [
                ...new Set(endorsementRates.map(r => r.endorsement)),
            ]);
            console.log(
                '[Endorsements] def endorsement names:',
                relevantDefs.map(d => d.endorsement)
            );
        }

        for (const def of relevantDefs) {
            const type = def.endorsement_type;
            if (!grouped.has(type)) grouped.set(type, []);
            const ratesForEndo = endorsementRates.filter(
                r => r.endorsement === def.endorsement
            );
            console.log(
                `[Endorsements] "${def.endorsement}" matched ${ratesForEndo.length} rates`
            );
            if (ratesForEndo.length > 0) {
                const ratesByCarrier = new Map<string, number>();
                ratesForEndo.forEach(r =>
                    ratesByCarrier.set(r.carrier, r.rate)
                );
                const existing = grouped.get(type)!;
                if (!existing.some(e => e.endorsement === def.endorsement)) {
                    existing.push({
                        endorsement: def.endorsement,
                        rates: ratesByCarrier,
                    });
                }
            }
        }

        return grouped;
    }, [
        endorsementDefs,
        endorsementRates,
        formData.selectedCarriers,
        formData.state,
        locationCropLines,
    ]);

    const getEndorsementRate = useCallback(
        (
            carrier: string,
            crop: string,
            county: string,
            form: string,
            endorsementName: string
        ): number | null =>
            findEndorsementRate(endorsementRates, {
                carrier,
                crop,
                county,
                form,
                endorsement: endorsementName,
                coverages: allCoveragesData,
            }),
        [endorsementRates, allCoveragesData]
    );

    const activeEndorsementCount = useMemo(
        () => selectedEndorsements.size,
        [selectedEndorsements]
    );

    // --- Table data: each product has Rate, Disc. Rate, Cost/Acre ---
    type ProductCell = {
        rateDisplay: string;
        discRateDisplay: string;
        costPerAcreDisplay: string;
        costPerAcreDiscDisplay: string;
    };
    type TableRowWithProducts = {
        carrier: string;
        products: Record<string, ProductCell>;
    };

    // Carrier → discount chip info for table display
    const discountChipMap = useMemo(() => {
        const map = new Map<string, { pct: string; due: string }>();
        const currentCrop = locationCropLines[0]?.crop ?? '';
        discountData.forEach(d => {
            if (d.has_discount && d.discount != null) {
                if (d.excluded_crops?.includes(currentCrop)) return;
                const info = {
                    pct:
                        (d.discount * 100).toFixed(
                            Number.isInteger(d.discount * 100) ? 0 : 1
                        ) + '%',
                    due: d.discount_deadline || '',
                };
                map.set(d.aip, info);
                if (d.aip === 'Pro Ag') {
                    map.set('proAg', info);
                    map.set('ProAg', info);
                }
            }
        });
        return map;
    }, [discountData, locationCropLines]);

    // Build discount info object for PDF rendering
    const pdfDiscountInfo = useMemo(() => {
        if (!applyCashDiscount) return undefined;
        const currentCrop = locationCropLines[0]?.crop ?? '';
        const info: Record<
            string,
            {
                fraction: number;
                pct: string;
                due: string;
                excluded_products?: string[];
            }
        > = {};
        discountData.forEach(d => {
            if (d.has_discount && d.discount != null) {
                if (d.excluded_crops?.includes(currentCrop)) return;
                const entry = {
                    fraction: d.discount,
                    pct:
                        (d.discount * 100).toFixed(
                            Number.isInteger(d.discount * 100) ? 0 : 1
                        ) + '%',
                    due: d.discount_deadline || '',
                    excluded_products: d.excluded_products ?? [],
                };
                info[d.aip] = entry;
                if (d.aip === 'Pro Ag') {
                    info['proAg'] = entry;
                    info['ProAg'] = entry;
                }
            }
        });
        return Object.keys(info).length > 0 ? info : undefined;
    }, [discountData, applyCashDiscount, locationCropLines]);

    // Build PDF endorsement props from current state
    const pdfSelectedEndorsements = useMemo(() => {
        if (selectedEndorsements.size === 0) return undefined;
        const record: Record<string, string> = {};
        selectedEndorsements.forEach((name, type) => {
            record[type] = name;
        });
        return record;
    }, [selectedEndorsements]);

    // Build PDF endorsement lookup keyed per (carrier, product, endorsement,
    // crop, county) so each location-crop line in the PDF resolves to its own
    // endorsement rate. Iterating over ratesByLine ensures every line picks
    // up its specific crop/county, not just the first location's.
    const pdfEndorsementRateLookup = useMemo(() => {
        if (endorsementRates.length === 0 || selectedEndorsements.size === 0)
            return undefined;
        const lookup: Record<string, number> = {};
        const selectedCarriers = formData.selectedCarriers || [];
        const selectedProducts = formData.selectedHailProducts || [];

        const seen = new Set<string>();
        const lineContexts =
            ratesByLine.length > 0
                ? ratesByLine.map(({ entry }) => ({
                      crop: entry?.crop ?? '',
                      county: entry?.county ?? '',
                  }))
                : [
                      {
                          crop: locationCropLines[0]?.crop ?? '',
                          county: locationCropLines[0]?.county ?? '',
                      },
                  ];

        for (const { crop, county } of lineContexts) {
            const dedupeKey = `${crop}|${county}`;
            if (seen.has(dedupeKey)) continue;
            seen.add(dedupeKey);
            for (const carrier of selectedCarriers) {
                for (const product of selectedProducts) {
                    selectedEndorsements.forEach((endorsementName, _type) => {
                        const rate = getEndorsementRate(
                            carrier,
                            crop,
                            county,
                            product,
                            endorsementName
                        );
                        if (rate !== null) {
                            const key = `${carrier}|${product}|${endorsementName}|${crop.toLowerCase()}|${county.toLowerCase()}`;
                            lookup[key] = rate;
                        }
                    });
                }
            }
        }
        return Object.keys(lookup).length > 0 ? lookup : undefined;
    }, [
        endorsementRates,
        selectedEndorsements,
        formData.selectedCarriers,
        formData.selectedHailProducts,
        ratesByLine,
        locationCropLines,
        getEndorsementRate,
    ]);

    const buildTableDataFromRates = useCallback(
        (
            rates: ProcessedRateData[],
            selectedProducts: string[],
            selectedCarriers: string[],
            ipa: number,
            discounts: DiscountLookup[],
            crop: string,
            county: string,
            endorsementLookup?: (
                carrier: string,
                crop: string,
                county: string,
                form: string,
                endorsementName: string
            ) => number | null,
            activeEndorsements?: Map<string, string>
        ): TableRowWithProducts[] => {
            if (
                selectedProducts.length === 0 ||
                selectedCarriers.length === 0
            ) {
                return [];
            }

            // Build carrier -> discount fraction map
            const discountMap = new Map<string, number>();
            // Build carrier -> excluded products map
            const productExclusionMap = new Map<string, string[]>();
            discounts.forEach(d => {
                if (d.has_discount && d.discount != null) {
                    // Skip if this crop is excluded from this AIP's discount
                    if (d.excluded_crops?.includes(crop)) return;
                    discountMap.set(d.aip, d.discount);
                    if (d.aip === 'Pro Ag') {
                        discountMap.set('proAg', d.discount);
                        discountMap.set('ProAg', d.discount);
                    }
                }
                if (d.excluded_products?.length > 0) {
                    productExclusionMap.set(d.aip, d.excluded_products);
                    if (d.aip === 'Pro Ag') {
                        productExclusionMap.set('proAg', d.excluded_products);
                        productExclusionMap.set('ProAg', d.excluded_products);
                    }
                }
            });

            const carrierGroups = new Map<
                string,
                Record<string, ProductCell>
            >();
            rates.forEach(record => {
                if (
                    typeof record.carrier === 'string' &&
                    typeof record.displayForm === 'string' &&
                    selectedCarriers.includes(record.carrier) &&
                    selectedProducts.includes(record.displayForm)
                ) {
                    const carrier = record.carrier;
                    const product = record.displayForm;
                    const rateNum = getRateAsNumber(record.selected_rate);

                    // Sum endorsement rates for this carrier/crop/county/product
                    let endorsementTotal = 0;
                    if (endorsementLookup && activeEndorsements) {
                        activeEndorsements.forEach((endorsementName, _type) => {
                            const endoRate = endorsementLookup(
                                carrier,
                                crop,
                                county,
                                product,
                                endorsementName
                            );
                            if (endoRate !== null) {
                                endorsementTotal += endoRate;
                            }
                        });
                    }

                    const totalRate = rateNum + endorsementTotal;
                    const costPerAcre = ipa * (totalRate / 100);

                    // Compute discount rate, zeroing out for excluded products
                    const carrierDiscFrac = discountMap.get(carrier) ?? 0;
                    const isProductExcluded =
                        productExclusionMap.get(carrier)?.includes(product) ??
                        false;
                    const discFrac = isProductExcluded ? 0 : carrierDiscFrac;
                    const discRateNum = totalRate * (1 - discFrac);
                    const discCostPerAcre = ipa * (discRateNum / 100);

                    if (!carrierGroups.has(carrier)) {
                        carrierGroups.set(carrier, {});
                    }
                    const products = carrierGroups.get(carrier)!;
                    products[product] = {
                        rateDisplay: formatRateValue(record.selected_rate),
                        discRateDisplay:
                            discFrac > 0
                                ? discRateNum.toFixed(2) + '%'
                                : formatRateValue(record.selected_rate),
                        costPerAcreDisplay: '$' + costPerAcre.toFixed(2),
                        costPerAcreDiscDisplay:
                            '$' + discCostPerAcre.toFixed(2),
                    };
                }
            });
            return Array.from(carrierGroups.entries()).map(
                ([carrier, products]) => ({
                    carrier,
                    products,
                })
            );
        },
        []
    );

    const tableData = useMemo(
        () =>
            buildTableDataFromRates(
                processedRates,
                formData.selectedHailProducts || [],
                formData.selectedCarriers || [],
                locationCropLines[0]?.ipa ?? formData.ipa ?? 1,
                discountData,
                locationCropLines[0]?.crop ?? '',
                locationCropLines[0]?.county ?? '',
                getEndorsementRate,
                selectedEndorsements
            ),
        [
            processedRates,
            formData.selectedHailProducts,
            formData.selectedCarriers,
            formData.ipa,
            locationCropLines,
            buildTableDataFromRates,
            discountData,
            getEndorsementRate,
            selectedEndorsements,
        ]
    );

    // Per-line table data for multi-line display
    const tableDataByLine = useMemo(() => {
        const selectedProducts = formData.selectedHailProducts || [];
        const selectedCarriers = formData.selectedCarriers || [];
        return ratesByLine.map(({ label, entry, rates }) => ({
            label,
            entry,
            tableData: buildTableDataFromRates(
                rates,
                selectedProducts,
                selectedCarriers,
                entry?.ipa ?? 1,
                discountData,
                entry?.crop ?? '',
                entry?.county ?? '',
                getEndorsementRate,
                selectedEndorsements
            ),
        }));
    }, [
        ratesByLine,
        formData.selectedHailProducts,
        formData.selectedCarriers,
        buildTableDataFromRates,
        discountData,
        getEndorsementRate,
        selectedEndorsements,
    ]);

    // PDF: when multiple location/crop lines, pass one table per line (title in merged row per table)
    const pdfLines = useMemo(() => {
        if (ratesByLine.length === 0) return undefined;
        return ratesByLine.map(({ label, entry, rates }) => ({
            label,
            ipa: entry?.ipa ?? 1,
            crop: entry?.crop ?? '',
            county: entry?.county ?? '',
            rates: rates.map(r => ({
                ...r,
                form: (r as ProcessedRateData).displayForm ?? r.form,
            })) as RateData[],
        }));
    }, [ratesByLine]);

    // --- Original displayedRates for PDF export (keep existing logic) ---
    const displayedRates = useMemo(() => {
        const selectedProducts = formData.selectedHailProducts || [];
        const selectedCarriers = formData.selectedCarriers || []; // Get selected carriers

        // If no products OR no carriers are selected, show no rates
        if (selectedProducts.length === 0 || selectedCarriers.length === 0) {
            return [];
        }

        // Filter the processed rates based on current selections
        const filtered = processedRates.filter(record => {
            // Check carrier selection
            const carrierMatch =
                typeof record.carrier === 'string' &&
                selectedCarriers.includes(record.carrier);
            // Check product selection (using the 'displayForm')
            const productMatch =
                typeof record.displayForm === 'string' &&
                selectedProducts.includes(record.displayForm);

            // Keep the record only if BOTH carrier and product match
            return carrierMatch && productMatch;
        });

        // Map back to RateData structure for the table (remove intermediate fields)
        return filtered.map(record => {
            // Destructure to remove temporary fields
            const { displayForm, aliasMatchFound, ...rest } = record;
            return {
                ...rest, // This contains fields from RateData
                form: displayForm, // Use the determined displayForm for the table's 'form' column
            } as RateData; // Assert type as RateData for the final output
        });
    }, [
        processedRates,
        formData.selectedHailProducts,
        formData.selectedCarriers,
    ]); // Re-run when base rates, product, or carrier selection changes

    // Effect to turn off updating indicator after displayedRates has recomputed
    useEffect(() => {
        if (isUpdatingDisplay) {
            // Enforce a minimum display time for the updating indicator.
            const timer = setTimeout(() => {
                setIsUpdatingDisplay(false);
            }, 1000); // Minimum 1 second
            return () => clearTimeout(timer);
        }
    }, [displayedRates, isUpdatingDisplay]);

    // Map processedRates back to RateData[] structure for consistent input to PDF
    const allRatesForLocation: RateData[] = useMemo(() => {
        return processedRates.map(record => {
            const { displayForm, aliasMatchFound, ...rest } = record;
            return { ...rest, form: displayForm } as RateData;
        });
    }, [processedRates]);

    // --- Set of forms currently displayed in the table ---
    const displayedFormsSet = useMemo(
        () => new Set(displayedRates.map(rate => rate.form)),
        [displayedRates]
    );

    // --- Calculate state for 'Select All' Carriers checkbox ---
    const numSelectedCarriers = formData.selectedCarriers?.length ?? 0;
    const isAllCarriersSelected =
        carrierOptions.length > 0 &&
        numSelectedCarriers === carrierOptions.length;
    const isCarriersIndeterminate =
        numSelectedCarriers > 0 && numSelectedCarriers < carrierOptions.length;

    // --- Calculate state for 'Select All' Hail Products checkbox ---
    const numSelectedProducts = formData.selectedHailProducts?.length ?? 0;
    const numAvailableHailProductOptions =
        availableHailProductOptionsForDisplay.length;
    const isAllProductsSelected =
        numAvailableHailProductOptions > 0 &&
        numSelectedProducts === numAvailableHailProductOptions;
    const isProductsIndeterminate =
        numSelectedProducts > 0 &&
        numSelectedProducts < numAvailableHailProductOptions;

    // --- Form Validation for Fetch Button ---
    const isFetchDisabled = useMemo(() => {
        // Check if all required location/crop fields have values
        const locationComplete =
            !!formData.state &&
            !!formData.county &&
            !!formData.township &&
            !!formData.range &&
            !!formData.crop;
        // Check if at least one carrier is selected
        const carrierSelected =
            formData.selectedCarriers && formData.selectedCarriers.length > 0;

        // Disable if any required part is missing
        return !locationComplete || !carrierSelected || isLoadingRates;
    }, [formData, isLoadingRates]);

    // --- Hydration from quote edit mode ---
    useEffect(() => {
        if (!editQuoteId) return;

        const hydrateFromQuote = async () => {
            hydratingRef.current = true;
            setHydratingFromQuote(true);
            try {
                const { data: quote } = await dataProvider.getOne<Quote>(
                    'quotes',
                    { id: editQuoteId }
                );
                setEditingQuoteRecord(quote);

                if (quote.quote_lines && quote.quote_lines.length > 0) {
                    // Build locationCropLines from quote_lines
                    const lines: LocationCropEntry[] = quote.quote_lines.map(
                        ql => ({
                            id: ql.id,
                            state: ql.state,
                            county: ql.county,
                            township: ql.township,
                            range: ql.range,
                            crop: ql.crop,
                            ipa: ql.ipa,
                        })
                    );
                    setLocationCropLines(lines);

                    // Set formData from first line + quote-level fields
                    const firstLine = quote.quote_lines[0];
                    setFormData(prev => ({
                        ...prev,
                        insuredName: quote.name || '',
                        state: firstLine.state || quote.state || '',
                        county: firstLine.county || quote.county || '',
                        township: firstLine.township || quote.township || '',
                        range: firstLine.range || quote.range || '',
                        crop: firstLine.crop || quote.crop || '',
                        ipa: firstLine.ipa || 1,
                        selectedCarriers: quote.selectedCarriers || [],
                        selectedHailProducts: quote.selectedHailProducts || [],
                    }));

                    // Build ratesByLine from quote_lines
                    const hydratedRatesByLine = quote.quote_lines.map(
                        (ql, index) => ({
                            lineIndex: index,
                            label: `${ql.county} - ${ql.crop} - ${ql.township} - ${ql.range} - IPA $${Number(ql.ipa).toFixed(2)}`,
                            entry: {
                                id: ql.id,
                                state: ql.state,
                                county: ql.county,
                                township: ql.township,
                                range: ql.range,
                                crop: ql.crop,
                                ipa: ql.ipa,
                            } as LocationCropEntry,
                            rates: ql.rates.map(
                                (r, rIdx) =>
                                    ({
                                        id: rIdx,
                                        carrier: r.carrier,
                                        form: r.originalForm,
                                        displayForm: r.displayForm,
                                        selected_rate: r.selected_rate,
                                        state: ql.state,
                                        county: ql.county,
                                        township: ql.township,
                                        range: ql.range,
                                        crop: ql.crop,
                                    }) as ProcessedRateData
                            ),
                        })
                    );
                    setRatesByLine(hydratedRatesByLine);

                    // Set processedRates from first line for legacy compat
                    if (hydratedRatesByLine.length > 0) {
                        setProcessedRates(hydratedRatesByLine[0].rates);
                    }
                } else if (quote.state) {
                    // Legacy quote with single-line HPT fields
                    setFormData(prev => ({
                        ...prev,
                        insuredName: quote.name || '',
                        state: quote.state || '',
                        county: quote.county || '',
                        township: quote.township || '',
                        range: quote.range || '',
                        crop: quote.crop || '',
                        selectedCarriers: quote.selectedCarriers || [],
                        selectedHailProducts: quote.selectedHailProducts || [],
                    }));
                }

                setIsEditMode(true);
                setFetchAttempted(true);
            } catch (error) {
                notify('Failed to load quote for editing.', {
                    type: 'error',
                });
                console.error('Error hydrating from quote:', error);
            } finally {
                setHydratingFromQuote(false);
                // Clear ref after a tick so cascading effects
                // don't reset our saved hail product selections
                setTimeout(() => {
                    hydratingRef.current = false;
                }, 500);
            }
        };

        hydrateFromQuote();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editQuoteId]);

    // Pre-select account when editing and options load
    useEffect(() => {
        if (
            editingQuoteRecord &&
            accountOptions.length > 0 &&
            !selectedFormAccount
        ) {
            const acc = accountOptions.find(
                a => a.id === editingQuoteRecord.account_id
            );
            if (acc) setSelectedFormAccount(acc);
        }
    }, [editingQuoteRecord, accountOptions, selectedFormAccount]);

    // --- Update Quote handler (edit mode) ---
    const handleUpdateQuote = async () => {
        if (!editingQuoteRecord || !identity?.id) return;
        setSaveQuoteSaving(true);
        try {
            // Build quote_lines from current state
            const quoteLines: QuoteLine[] =
                ratesByLine.length > 0
                    ? ratesByLine.map(({ entry, rates }) => ({
                          id:
                              entry?.id ??
                              `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                          state: entry?.state ?? formData.state ?? '',
                          county: entry?.county ?? formData.county ?? '',
                          township: entry?.township ?? formData.township ?? '',
                          range: entry?.range ?? formData.range ?? '',
                          crop: entry?.crop ?? formData.crop ?? '',
                          ipa: entry?.ipa ?? 1,
                          selectedProduct: null,
                          selectedCarrier: null,
                          selectedProducts: [],
                          rates: rates.map(r => ({
                              carrier: r.carrier ?? '',
                              displayForm:
                                  (r as ProcessedRateData).displayForm ??
                                  r.form ??
                                  '',
                              originalForm: r.form ?? '',
                              selected_rate: r.selected_rate ?? null,
                              class: '',
                              favorite: false,
                          })),
                      }))
                    : processedRates.length > 0
                      ? [
                            {
                                id: `line-${Date.now()}`,
                                state: formData.state ?? '',
                                county: formData.county ?? '',
                                township: formData.township ?? '',
                                range: formData.range ?? '',
                                crop: formData.crop ?? '',
                                ipa: formData.ipa ?? 1,
                                selectedProduct: null,
                                selectedCarrier: null,
                                selectedProducts: [],
                                rates: processedRates.map(r => ({
                                    carrier: r.carrier ?? '',
                                    displayForm: r.displayForm ?? r.form ?? '',
                                    originalForm: r.form ?? '',
                                    selected_rate: r.selected_rate ?? null,
                                    class: '',
                                    favorite: false,
                                })),
                            },
                        ]
                      : [];

            await dataProvider.update('quotes', {
                id: editingQuoteRecord.id,
                data: {
                    quote_lines: quoteLines,
                    state: formData.state,
                    county: formData.county,
                    township: formData.township,
                    range: formData.range,
                    crop: formData.crop,
                    selectedCarriers: formData.selectedCarriers,
                    selectedHailProducts: formData.selectedHailProducts,
                    paymentMethod: applyCashDiscount
                        ? 'Cash Discount (when applicable)'
                        : 'Premium Billing Date (no discount)',
                    name:
                        formData.insuredName?.trim() || editingQuoteRecord.name,
                    updated_at: new Date().toISOString(),
                },
                previousData: editingQuoteRecord,
            });

            notify('Quote updated successfully.', {
                type: 'success',
            });
            redirect('show', 'quotes', editingQuoteRecord.id);
        } catch (error) {
            notify(
                error && typeof error === 'object' && 'message' in error
                    ? String((error as Error).message)
                    : 'Failed to update quote.',
                { type: 'error' }
            );
        } finally {
            setSaveQuoteSaving(false);
        }
    };

    // --- Handlers for PDF Modal ---
    const handleOpenPdfPreview = () => {
        if (displayedRates.length > 0) {
            setPdfPreviewOpen(true);
        } else {
            notify('No rates to export.', { type: 'warning' });
        }
    };

    const handleClosePdfPreview = () => {
        setPdfPreviewOpen(false);
    };

    const handleOpenSaveQuoteDialog = () => {
        if (selectedFormAccount) {
            setSaveQuoteAccountId(selectedFormAccount.id as number);
            setSaveQuoteAccountName(selectedFormAccount.name ?? '');
        } else {
            setSaveQuoteAccountId(null);
            setSaveQuoteAccountName('');
        }
        setSaveQuoteContactId(null);
        setSaveQuoteContactName('');
        setSaveQuoteDialogOpen(true);
    };

    const handleCloseSaveQuoteDialog = () => {
        setSaveQuoteDialogOpen(false);
        setSaveQuoteAccountId(null);
        setSaveQuoteAccountName('');
        setSaveQuoteContactId(null);
        setSaveQuoteContactName('');
    };

    const handleCreateAccountFromDialog = async () => {
        const name = createAccountName.trim();
        if (!name || !identity?.id) return;
        setCreateAccountSaving(true);
        try {
            const { data } = await dataProvider.create('accounts', {
                data: { name, sales_id: identity.id },
            });
            const newAccount = { id: data.id, name: data.name } as Account;
            setExtraAccounts(prev => [...prev, newAccount]);
            if (createAccountFromSaveQuote) {
                // Auto-select in Save as Quote dialog
                setSaveQuoteAccountId(newAccount.id as number);
                setSaveQuoteAccountName(newAccount.name);
                setSaveQuoteContactId(null);
                setSaveQuoteContactName('');
                setCreateAccountFromSaveQuote(false);
            } else {
                setSelectedFormAccount(newAccount);
            }
            setCreateAccountDialogOpen(false);
            setCreateAccountName('');
            notify('Account created.', { type: 'success' });
        } catch (error) {
            notify(
                error && typeof error === 'object' && 'message' in error
                    ? String((error as Error).message)
                    : 'Failed to create account.',
                { type: 'error' }
            );
        } finally {
            setCreateAccountSaving(false);
        }
    };

    const handleCreateAccountFromAutocomplete = async (name: string) => {
        if (!name.trim() || !identity?.id) return;
        setCreateAccountSaving(true);
        try {
            const { data } = await dataProvider.create('accounts', {
                data: { name: name.trim(), sales_id: identity.id },
            });
            const newAccount = { id: data.id, name: data.name } as Account;
            setExtraAccounts(prev => [...prev, newAccount]);
            setSelectedFormAccount(newAccount);
            setFormAccountInputValue('');
            notify('Account created.', { type: 'success' });
        } catch (error) {
            notify(
                error && typeof error === 'object' && 'message' in error
                    ? String((error as Error).message)
                    : 'Failed to create account.',
                { type: 'error' }
            );
        } finally {
            setCreateAccountSaving(false);
        }
    };

    const handleCreateContactFromDialog = async () => {
        const first = createContactFirstName.trim();
        const last = createContactLastName.trim();
        if (!first || !last || !identity?.id || !saveQuoteAccountId) return;
        setCreateContactSaving(true);
        try {
            const { data } = await dataProvider.create('contacts', {
                data: {
                    first_name: first,
                    last_name: last,
                    account_id: saveQuoteAccountId,
                    sales_id: identity.id,
                    first_seen: new Date().toISOString(),
                    last_seen: new Date().toISOString(),
                    email_jsonb: [],
                    phone_jsonb: [],
                    tags: [],
                },
            });
            const newContact = {
                id: data.id,
                first_name: first,
                last_name: last,
                account_id: saveQuoteAccountId,
            } as Contact;
            setExtraContacts(prev => [...prev, newContact]);
            setSaveQuoteContactId(data.id as number);
            setSaveQuoteContactName(`${first} ${last}`);
            setCreateContactDialogOpen(false);
            setCreateContactFirstName('');
            setCreateContactLastName('');
            notify('Contact created.', { type: 'success' });
        } catch (error) {
            notify(
                error && typeof error === 'object' && 'message' in error
                    ? String((error as Error).message)
                    : 'Failed to create contact.',
                { type: 'error' }
            );
        } finally {
            setCreateContactSaving(false);
        }
    };

    const handleSaveAsQuote = async () => {
        if (!identity?.id || !saveQuoteAccountId) {
            notify('Please select an account to save this quote to.', {
                type: 'warning',
            });
            return;
        }
        setSaveQuoteSaving(true);
        try {
            const now = new Date().toISOString();
            const quoteName =
                formData.insuredName?.trim() ||
                `HPT - ${formData.county || 'NA'} - ${formData.crop || 'NA'}`;
            // Build quote_lines snapshot from current state
            const quoteLines: QuoteLine[] =
                ratesByLine.length > 0
                    ? ratesByLine.map(({ entry, rates }) => ({
                          id:
                              entry?.id ??
                              `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                          state: entry?.state ?? formData.state ?? '',
                          county: entry?.county ?? formData.county ?? '',
                          township: entry?.township ?? formData.township ?? '',
                          range: entry?.range ?? formData.range ?? '',
                          crop: entry?.crop ?? formData.crop ?? '',
                          ipa: entry?.ipa ?? 1,
                          selectedProduct: null,
                          selectedCarrier: null,
                          selectedProducts: [],
                          rates: rates.map(r => ({
                              carrier: r.carrier ?? '',
                              displayForm:
                                  (r as ProcessedRateData).displayForm ??
                                  r.form ??
                                  '',
                              originalForm: r.form ?? '',
                              selected_rate: r.selected_rate ?? null,
                              class: '',
                              favorite: false,
                          })),
                      }))
                    : processedRates.length > 0
                      ? [
                            {
                                id: `line-${Date.now()}`,
                                state: formData.state ?? '',
                                county: formData.county ?? '',
                                township: formData.township ?? '',
                                range: formData.range ?? '',
                                crop: formData.crop ?? '',
                                ipa: formData.ipa ?? 1,
                                selectedProduct: null,
                                selectedCarrier: null,
                                selectedProducts: [],
                                rates: processedRates.map(r => ({
                                    carrier: r.carrier ?? '',
                                    displayForm: r.displayForm ?? r.form ?? '',
                                    originalForm: r.form ?? '',
                                    selected_rate: r.selected_rate ?? null,
                                    class: '',
                                    favorite: false,
                                })),
                            },
                        ]
                      : [];

            const payload = {
                name: quoteName,
                account_id: saveQuoteAccountId,
                contact_ids: saveQuoteContactId ? [saveQuoteContactId] : [],
                category: quoteCategories?.[0] ?? '',
                stage: quoteStages?.[0]?.value ?? 'opportunity',
                description: '',
                amount: 0,
                created_at: now,
                updated_at: now,
                expected_closing_date: null,
                sales_id: identity.id,
                index: 0,
                state: formData.state ?? undefined,
                county: formData.county ?? undefined,
                township: formData.township ?? undefined,
                range: formData.range ?? undefined,
                crop: formData.crop ?? undefined,
                active: true,
                selectedCarriers: formData.selectedCarriers ?? [],
                selectedHailProducts: formData.selectedHailProducts ?? [],
                selectedEndorsements: pdfSelectedEndorsements ?? {},
                endorsementRateLookup: pdfEndorsementRateLookup ?? {},
                paymentMethod: applyCashDiscount
                    ? 'Cash Discount (when applicable)'
                    : 'Premium Billing Date (no discount)',
                notes: '',
                opportunityOwnerId: identity.id,
                quote_lines: quoteLines,
            };
            const { data: created } = await dataProvider.create('quotes', {
                data: payload,
            });
            notify('Quote saved successfully.', { type: 'success' });
            handleCloseSaveQuoteDialog();
            redirect(`/quotes/${created?.id}/page`);
        } catch (error) {
            notify(
                error && typeof error === 'object' && 'message' in error
                    ? String((error as Error).message)
                    : 'Failed to save quote.',
                { type: 'error' }
            );
        } finally {
            setSaveQuoteSaving(false);
        }
    };

    // Handle identity loading/error states
    if (identityLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '80vh',
                }}
            >
                <Loading />
            </Box>
        );
    }

    if (hydratingFromQuote) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '80vh',
                }}
            >
                <Loading />
            </Box>
        );
    }

    if (identityError) {
        return <Alert severity="error">Error loading user information.</Alert>;
    }

    if (!identity) {
        // Should not happen if requireAuth is true, but good practice
        return <Alert severity="warning">Could not load user identity.</Alert>;
    }

    // --- DEBUG LOGS ---
    // console.log('[HailPricingToolPage] User Identity:', identity);
    // console.log('[HailPricingToolPage] Crop West Logo URL:', cropWestLogoUrl);
    // --- END DEBUG LOGS ---

    return (
        <Box
            sx={{
                p: isSmallScreen ? 2 : 3,
            }}
        >
            <Typography variant="h5" sx={{ mb: isSmallScreen ? 2 : 3 }}>
                Hail Pricing Tool
            </Typography>

            <Paper elevation={3} sx={{ p: isSmallScreen ? 2 : 3 }}>
                <Box component="form" noValidate autoComplete="off">
                    {/* Account & Quote Name */}
                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: 'medium' }}
                    >
                        Account & Quote
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Autocomplete<AccountOrCreate>
                            sx={{ flex: 1 }}
                            value={
                                selectedFormAccount as AccountOrCreate | null
                            }
                            inputValue={formAccountInputValue}
                            onInputChange={(_, newValue) =>
                                setFormAccountInputValue(newValue)
                            }
                            options={(() => {
                                const list: AccountOrCreate[] = [
                                    ...accountOptions,
                                    ...extraAccounts,
                                ];
                                const trimmed = formAccountInputValue.trim();
                                const filtered = trimmed
                                    ? list.filter(
                                          (a): a is Account =>
                                              !('__create' in a) &&
                                              a.name
                                                  ?.toLowerCase()
                                                  .includes(
                                                      trimmed.toLowerCase()
                                                  )
                                      )
                                    : list;
                                if (trimmed.length > 0) {
                                    const exists = list.some(
                                        a =>
                                            !('__create' in a) &&
                                            (
                                                a as Account
                                            ).name?.toLowerCase() ===
                                                trimmed.toLowerCase()
                                    );
                                    if (!exists) {
                                        return [
                                            ...filtered,
                                            {
                                                __create: true as const,
                                                name: trimmed,
                                            },
                                        ];
                                    }
                                }
                                return filtered;
                            })()}
                            getOptionLabel={option =>
                                '__create' in option && option.__create
                                    ? `Create "${option.name}"`
                                    : option.name ?? ''
                            }
                            isOptionEqualToValue={(option, value) => {
                                if ('__create' in option && option.__create)
                                    return false;
                                if ('__create' in value && value.__create)
                                    return false;
                                return (
                                    (option as Account).id ===
                                    (value as Account).id
                                );
                            }}
                            onChange={(_, newValue: AccountOrCreate | null) => {
                                if (!newValue) {
                                    setSelectedFormAccount(null);
                                    return;
                                }
                                if (
                                    '__create' in newValue &&
                                    newValue.__create
                                ) {
                                    handleCreateAccountFromAutocomplete(
                                        newValue.name
                                    );
                                    return;
                                }
                                setSelectedFormAccount(newValue as Account);
                            }}
                            filterSelectedOptions
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Account"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {createAccountSaving ? (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{ mr: 1 }}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setCreateAccountName('');
                                setCreateAccountDialogOpen(true);
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            New account
                        </Button>
                    </Stack>
                    <TextField
                        name="insuredName"
                        label="Quote Name"
                        value={formData.insuredName || ''}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    />
                    {/* Location & Crop Info */}
                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: 'medium' }}
                    >
                        Location & Crop
                    </Typography>
                    <Grid
                        container
                        spacing={isSmallScreen ? 1.5 : 2}
                        sx={{ mb: 2 }}
                    >
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="state-select-label">
                                    State
                                </InputLabel>
                                <Select
                                    labelId="state-select-label"
                                    name="state"
                                    value={formData.state || ''}
                                    label="State"
                                    onChange={handleSelectChange}
                                    required
                                >
                                    {states.map(state => (
                                        <MenuItem key={state} value={state}>
                                            {state}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={!formData.state || loadingCounties}
                            >
                                <InputLabel shrink={true}>County</InputLabel>
                                <Select
                                    required
                                    name="county"
                                    value={formData.county || ''}
                                    label="County"
                                    onChange={handleSelectChange}
                                    displayEmpty
                                    inputProps={{
                                        endAdornment: loadingCounties ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{ mr: 2.5 }}
                                            >
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                >
                                    <MenuItem
                                        value=""
                                        disabled={loadingCounties}
                                    >
                                        {loadingCounties
                                            ? 'Loading Counties...'
                                            : formData.state
                                              ? 'Select County...'
                                              : 'Select State First'}
                                    </MenuItem>
                                    {countyOptions.map(county => (
                                        <MenuItem key={county} value={county}>
                                            {county}
                                        </MenuItem>
                                    ))}
                                    {!loadingCounties &&
                                        formData.state &&
                                        countyOptions.length === 0 && (
                                            <MenuItem value="" disabled>
                                                No counties found
                                            </MenuItem>
                                        )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={!formData.county || loadingTownships}
                            >
                                <InputLabel shrink={true}>Township</InputLabel>
                                <Select
                                    required
                                    name="township"
                                    value={formData.township || ''}
                                    label="Township"
                                    onChange={handleSelectChange}
                                    displayEmpty
                                    inputProps={{
                                        endAdornment: loadingTownships ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{ mr: 2.5 }}
                                            >
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                >
                                    <MenuItem
                                        value=""
                                        disabled={loadingTownships}
                                    >
                                        {loadingTownships
                                            ? 'Loading...'
                                            : formData.county
                                              ? 'Select Township...'
                                              : 'Select County First'}
                                    </MenuItem>
                                    {townshipOptions.map(township => (
                                        <MenuItem
                                            key={township}
                                            value={township}
                                        >
                                            {township}
                                        </MenuItem>
                                    ))}
                                    {!loadingTownships &&
                                        formData.county &&
                                        townshipOptions.length === 0 && (
                                            <MenuItem value="" disabled>
                                                No townships found
                                            </MenuItem>
                                        )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={!formData.township || loadingRanges}
                            >
                                <InputLabel shrink={true}>Range</InputLabel>
                                <Select
                                    required
                                    name="range"
                                    value={formData.range || ''}
                                    label="Range"
                                    onChange={handleSelectChange}
                                    displayEmpty
                                    inputProps={{
                                        endAdornment: loadingRanges ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{ mr: 2.5 }}
                                            >
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                >
                                    <MenuItem value="" disabled={loadingRanges}>
                                        {loadingRanges
                                            ? 'Loading...'
                                            : formData.township
                                              ? 'Select Range...'
                                              : 'Select Township First'}
                                    </MenuItem>
                                    {rangeOptions.map(range => (
                                        <MenuItem key={range} value={range}>
                                            {range}
                                        </MenuItem>
                                    ))}
                                    {!loadingRanges &&
                                        formData.township &&
                                        rangeOptions.length === 0 && (
                                            <MenuItem value="" disabled>
                                                No ranges found
                                            </MenuItem>
                                        )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={!formData.state || loadingCrops}
                            >
                                <InputLabel shrink={true}>Crop</InputLabel>
                                <Select
                                    required
                                    name="crop"
                                    value={formData.crop || ''}
                                    label="Crop"
                                    onChange={handleSelectChange}
                                    displayEmpty
                                    inputProps={{
                                        endAdornment: loadingCrops ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{ mr: 2.5 }}
                                            >
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                >
                                    <MenuItem value="" disabled={loadingCrops}>
                                        {loadingCrops
                                            ? 'Loading...'
                                            : formData.state
                                              ? 'Select Crop...'
                                              : 'Select State First'}
                                    </MenuItem>
                                    {cropOptions.map(item => {
                                        // Build carrier class label, e.g. "(Rain & Hail E, RCIS E2)"
                                        const classLabel =
                                            item.carrierClasses.length > 0
                                                ? ` (${item.carrierClasses.map(cc => `${cc.aip} ${cc.classes.join('/')}`).join(', ')})`
                                                : '';
                                        return (
                                            <MenuItem
                                                key={item.crop}
                                                value={item.crop}
                                            >
                                                {item.crop}
                                                {classLabel && (
                                                    <span
                                                        style={{
                                                            color: '#888',
                                                            fontSize: '0.85em',
                                                            marginLeft: '4px',
                                                        }}
                                                    >
                                                        {classLabel}
                                                    </span>
                                                )}
                                            </MenuItem>
                                        );
                                    })}
                                    {!loadingCrops &&
                                        formData.state &&
                                        cropOptions.length === 0 && (
                                            <MenuItem value="" disabled>
                                                No crops found
                                            </MenuItem>
                                        )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="IPA"
                                type="text"
                                inputMode="decimal"
                                inputProps={{
                                    min: 0,
                                    step: 0.01,
                                }}
                                value={
                                    ipaFieldFocused
                                        ? ipaInputStr
                                        : String(
                                              formData.ipa ??
                                                  locationCropLines[0]?.ipa ??
                                                  1
                                          )
                                }
                                onFocus={() => {
                                    setIpaFieldFocused(true);
                                    setIpaInputStr(
                                        String(
                                            formData.ipa ??
                                                locationCropLines[0]?.ipa ??
                                                1
                                        )
                                    );
                                }}
                                onChange={e => setIpaInputStr(e.target.value)}
                                onBlur={() => {
                                    const n = parseFloat(ipaInputStr);
                                    const num =
                                        Number.isNaN(n) || n < 0 ? 1 : n;
                                    setFormData(prev => ({
                                        ...prev,
                                        ipa: num,
                                    }));
                                    setLocationCropLines(prev => {
                                        const next = [...prev];
                                        if (next[0])
                                            next[0] = {
                                                ...next[0],
                                                ipa: num,
                                            };
                                        return next;
                                    });
                                    setIpaFieldFocused(false);
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                    </Grid>

                    {/* Additional Location & Crop lines */}
                    {locationCropLines.slice(1).map((entry, idx) => {
                        const lineIndex = idx + 1;
                        const stateAbbr = entry.state
                            ? stateAbbreviationMap[entry.state]
                            : undefined;
                        const countyOpts =
                            stateAbbr && countyCache[stateAbbr]
                                ? countyCache[stateAbbr]
                                : [];
                        const townshipKey =
                            stateAbbr && entry.county
                                ? cacheKey(stateAbbr, entry.county)
                                : '';
                        const townshipOpts = townshipKey
                            ? townshipCache[townshipKey] ?? []
                            : [];
                        const rangeKey =
                            stateAbbr && entry.county && entry.township
                                ? cacheKey(
                                      stateAbbr,
                                      entry.county,
                                      entry.township
                                  )
                                : '';
                        const rangeOpts = rangeKey
                            ? rangeCache[rangeKey] ?? []
                            : [];
                        const cropOpts =
                            stateAbbr && cropCache[stateAbbr]
                                ? cropCache[stateAbbr]
                                : [];
                        return (
                            <Paper
                                key={entry.id}
                                variant="outlined"
                                sx={{ p: 2, mb: 2 }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography variant="subtitle2">
                                        Location & Crop #{lineIndex + 1}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            removeLocationCropLine(lineIndex)
                                        }
                                        aria-label="Remove this location"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Grid
                                    container
                                    spacing={isSmallScreen ? 1.5 : 2}
                                >
                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>State</InputLabel>
                                            <Select
                                                value={entry.state || ''}
                                                label="State"
                                                onChange={e =>
                                                    updateLocationCropLine(
                                                        lineIndex,
                                                        {
                                                            state: e.target
                                                                .value,
                                                            county: '',
                                                            township: '',
                                                            range: '',
                                                            crop: '',
                                                        }
                                                    )
                                                }
                                            >
                                                {states.map(s => (
                                                    <MenuItem key={s} value={s}>
                                                        {s}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            disabled={!entry.state}
                                        >
                                            <InputLabel>County</InputLabel>
                                            <Select
                                                value={entry.county || ''}
                                                label="County"
                                                onChange={e =>
                                                    updateLocationCropLine(
                                                        lineIndex,
                                                        {
                                                            county: e.target
                                                                .value,
                                                            township: '',
                                                            range: '',
                                                        }
                                                    )
                                                }
                                            >
                                                {countyOpts.map(c => (
                                                    <MenuItem key={c} value={c}>
                                                        {c}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            disabled={!entry.county}
                                        >
                                            <InputLabel>Township</InputLabel>
                                            <Select
                                                value={entry.township || ''}
                                                label="Township"
                                                onChange={e =>
                                                    updateLocationCropLine(
                                                        lineIndex,
                                                        {
                                                            township:
                                                                e.target.value,
                                                            range: '',
                                                        }
                                                    )
                                                }
                                            >
                                                {townshipOpts.map(t => (
                                                    <MenuItem key={t} value={t}>
                                                        {t}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            disabled={!entry.township}
                                        >
                                            <InputLabel>Range</InputLabel>
                                            <Select
                                                value={entry.range || ''}
                                                label="Range"
                                                onChange={e =>
                                                    updateLocationCropLine(
                                                        lineIndex,
                                                        {
                                                            range: e.target
                                                                .value,
                                                        }
                                                    )
                                                }
                                            >
                                                {rangeOpts.map(r => (
                                                    <MenuItem key={r} value={r}>
                                                        {r}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            disabled={!entry.state}
                                        >
                                            <InputLabel>Crop</InputLabel>
                                            <Select
                                                value={entry.crop || ''}
                                                label="Crop"
                                                onChange={e =>
                                                    updateLocationCropLine(
                                                        lineIndex,
                                                        {
                                                            crop: e.target
                                                                .value,
                                                        }
                                                    )
                                                }
                                            >
                                                {cropOpts.map(opt => {
                                                    const classLabel =
                                                        opt.carrierClasses
                                                            .length > 0
                                                            ? ` (${opt.carrierClasses.map(cc => `${cc.aip} ${cc.classes.join('/')}`).join(', ')})`
                                                            : '';
                                                    return (
                                                        <MenuItem
                                                            key={opt.crop}
                                                            value={opt.crop}
                                                        >
                                                            {opt.crop}
                                                            {classLabel && (
                                                                <span
                                                                    style={{
                                                                        color: '#888',
                                                                        fontSize:
                                                                            '0.85em',
                                                                        marginLeft:
                                                                            '4px',
                                                                    }}
                                                                >
                                                                    {classLabel}
                                                                </span>
                                                            )}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            label="IPA"
                                            type="text"
                                            inputMode="decimal"
                                            inputProps={{
                                                min: 0,
                                                step: 0.01,
                                            }}
                                            value={
                                                editingIpaLineIndex ===
                                                lineIndex
                                                    ? editingIpaStr
                                                    : String(
                                                          entry.ipa ??
                                                              locationCropLines[0]
                                                                  ?.ipa ??
                                                              1
                                                      )
                                            }
                                            onFocus={() => {
                                                setEditingIpaLineIndex(
                                                    lineIndex
                                                );
                                                setEditingIpaStr(
                                                    String(
                                                        entry.ipa ??
                                                            locationCropLines[0]
                                                                ?.ipa ??
                                                            1
                                                    )
                                                );
                                            }}
                                            onChange={e =>
                                                setEditingIpaStr(e.target.value)
                                            }
                                            onBlur={() => {
                                                const n =
                                                    parseFloat(editingIpaStr);
                                                const num =
                                                    Number.isNaN(n) || n < 0
                                                        ? 1
                                                        : n;
                                                updateLocationCropLine(
                                                    lineIndex,
                                                    { ipa: num }
                                                );
                                                setEditingIpaLineIndex(null);
                                            }}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addLocationCropLine}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 2 }}
                    >
                        Add location & crop
                    </Button>

                    <Divider sx={{ my: 3 }} />

                    {/* Carriers Section - Now uses carrierOptions */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 'medium' }}
                        >
                            Carriers *
                        </Typography>
                        {!loadingCarriers && carrierOptions.length > 0 && (
                            <FormControlLabel
                                label="Select All"
                                control={
                                    <Checkbox
                                        checked={isAllCarriersSelected}
                                        indeterminate={isCarriersIndeterminate}
                                        onChange={handleSelectAllCarriersChange}
                                        size="small"
                                        sx={{
                                            color: 'grey.400',
                                            '&.Mui-checked': {
                                                color: '#508A88',
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: '#508A88',
                                            },
                                        }}
                                    />
                                }
                                sx={{
                                    mr: 0,
                                    '& .MuiTypography-root': {
                                        fontSize: '0.85rem',
                                    },
                                }}
                            />
                        )}
                    </Box>
                    {/* Show loading indicator */}
                    {loadingCarriers && (
                        <CircularProgress size={20} sx={{ mb: 1 }} />
                    )}
                    {/* Render checkboxes only when options are loaded */}
                    {!loadingCarriers && carrierOptions.length > 0 && (
                        <FormGroup
                            row={!isSmallScreen}
                            sx={{
                                mb: 1,
                                '& .MuiFormControlLabel-root': {
                                    mb: isSmallScreen ? 1 : 0,
                                    mr: isSmallScreen ? 0 : 2,
                                },
                            }}
                        >
                            {carrierOptions.map(carrier => (
                                <FormControlLabel
                                    key={carrier}
                                    control={
                                        <Checkbox
                                            checked={formData.selectedCarriers?.includes(
                                                carrier
                                            )}
                                            onChange={e =>
                                                handleCheckboxChange(
                                                    e,
                                                    'selectedCarriers'
                                                )
                                            }
                                            name={carrier}
                                            size="small"
                                            sx={{
                                                color: 'grey.400',
                                                '&.Mui-checked': {
                                                    color: '#508A88',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        carrier === 'proAg' ? 'Pro Ag' : carrier
                                    }
                                />
                            ))}
                        </FormGroup>
                    )}
                    {!loadingCarriers && carrierOptions.length === 0 && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontStyle: 'italic', mb: 1 }}
                        >
                            No carriers available.
                        </Typography>
                    )}
                    {/* Selected Chips display remains the same */}
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                    >
                        Selected:
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: isSmallScreen ? 0.5 : 1,
                            mt: 0.5,
                            minHeight: '24px',
                        }}
                    >
                        {formData.selectedCarriers?.map(carrier => (
                            <Chip
                                key={carrier}
                                label={carrier === 'proAg' ? 'Pro Ag' : carrier}
                                onDelete={() =>
                                    handleChipSelection(
                                        carrier,
                                        'selectedCarriers'
                                    )
                                }
                                size="small"
                                sx={{
                                    borderRadius: '16px',
                                    bgcolor: '#508A88',
                                    color: 'white',
                                    mb: isSmallScreen ? 0.5 : 0,
                                    '& .MuiChip-deleteIcon': {
                                        color: 'white',
                                        '&:hover': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                    },
                                    '& .MuiChip-icon': {
                                        color: 'white',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: 'white',
                                    },
                                }}
                            />
                        ))}
                        {(formData.selectedCarriers?.length ?? 0) === 0 && (
                            <Typography
                                variant="caption"
                                sx={{ fontStyle: 'italic' }}
                            >
                                None
                            </Typography>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Hail Products Section - Modified conditional rendering */}
                    {fetchAttempted && !fetchError && (
                        <Box>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 1 }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 'medium' }}
                                >
                                    Hail Products *
                                </Typography>
                                {/* Select All Checkbox - Render conditionally */}
                                {!isLoadingRates && // Only show if not initial loading
                                    availableHailProductOptionsForDisplay.length >
                                        0 && (
                                        <FormControlLabel
                                            label="Select All"
                                            control={
                                                <Checkbox
                                                    checked={
                                                        isAllProductsSelected
                                                    }
                                                    indeterminate={
                                                        isProductsIndeterminate
                                                    }
                                                    onChange={
                                                        handleSelectAllProductsChange
                                                    }
                                                    size="small"
                                                    disabled={isLoadingRates} // Disable if initial loading
                                                    sx={{
                                                        color: 'grey.400',
                                                        '&.Mui-checked': {
                                                            color: '#508A88',
                                                        },
                                                        '&.MuiCheckbox-indeterminate':
                                                            {
                                                                color: '#508A88',
                                                            },
                                                    }}
                                                />
                                            }
                                        />
                                    )}
                            </Stack>

                            {isLoadingRates ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <CircularProgress
                                        size={20}
                                        sx={{ mr: 1 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Loading available products...
                                    </Typography>
                                </Box>
                            ) : availableHailProductOptionsForDisplay.length >
                              0 ? (
                                <FormGroup
                                    row={!isSmallScreen}
                                    sx={{
                                        mb: 1,
                                        '& .MuiFormControlLabel-root': {
                                            mb: isSmallScreen ? 1 : 0,
                                            mr: isSmallScreen ? 0 : 2,
                                        },
                                    }}
                                >
                                    {availableHailProductOptionsForDisplay.map(
                                        product => (
                                            <FormControlLabel
                                                key={product}
                                                control={
                                                    <Checkbox
                                                        checked={formData.selectedHailProducts?.includes(
                                                            product
                                                        )}
                                                        onChange={e =>
                                                            handleCheckboxChange(
                                                                e,
                                                                'selectedHailProducts'
                                                            )
                                                        }
                                                        name={product}
                                                        size="small"
                                                        sx={{
                                                            color: 'grey.400',
                                                            '&.Mui-checked': {
                                                                color: '#508A88',
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={product}
                                            />
                                        )
                                    )}
                                </FormGroup>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic', mb: 1 }}
                                >
                                    No hail products available for the fetched
                                    rates criteria.
                                </Typography>
                            )}

                            {!isLoadingRates &&
                                availableHailProductOptionsForDisplay.length >
                                    0 &&
                                (!formData.selectedHailProducts ||
                                    formData.selectedHailProducts.length ===
                                        0) && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 1 }}
                                    >
                                        Select one or more hail products to view
                                        matching rates.
                                    </Typography>
                                )}

                            {/* Selected Chips (remains the same, but now inside the main conditional) */}
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                            >
                                Selected:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: isSmallScreen ? 0.5 : 1,
                                    mt: 0.5,
                                    minHeight: '24px',
                                }}
                            >
                                {formData.selectedHailProducts?.map(product => {
                                    const hasRate =
                                        displayedFormsSet.has(product);
                                    return (
                                        <Chip
                                            key={product}
                                            label={product}
                                            icon={
                                                hasRate ? (
                                                    <CheckCircleOutlineIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            marginLeft: '5px',
                                                        }}
                                                    />
                                                ) : undefined
                                            }
                                            onDelete={() =>
                                                handleChipSelection(
                                                    product,
                                                    'selectedHailProducts'
                                                )
                                            }
                                            size="small"
                                            sx={{
                                                borderRadius: '16px',
                                                bgcolor: '#508A88',
                                                color: 'white',
                                                mb: isSmallScreen ? 0.5 : 0,
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'white',
                                                    '&:hover': {
                                                        color: 'rgba(255, 255, 255, 0.7)',
                                                    },
                                                },
                                                '& .MuiChip-icon': {
                                                    color: 'white',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    );
                                })}
                                {(formData.selectedHailProducts?.length ??
                                    0) === 0 && (
                                    <Typography
                                        variant="caption"
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        None
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Endorsement Selection */}
                    {availableEndorsements.size > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Endorsements
                            </Typography>
                            {(formData.selectedCarriers || []).includes(
                                'NAU'
                            ) && (
                                <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                                    FYI — NAU Requires a mandatory Wind Shatter
                                    XS10 Endorsement for Wheat, Barley, Malting
                                    Barley, and/or Triticale — make sure you add
                                    it when quoting those crops.
                                </Alert>
                            )}
                            {Array.from(availableEndorsements.entries()).map(
                                ([type, options]) => (
                                    <Box
                                        key={type}
                                        sx={{
                                            mb: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ minWidth: 120 }}
                                        >
                                            {type
                                                .replace(/_/g, ' ')
                                                .replace(/\b\w/g, c =>
                                                    c.toUpperCase()
                                                )}
                                            :
                                        </Typography>
                                        <RadioGroup
                                            value={
                                                selectedEndorsements.get(
                                                    type
                                                ) || ''
                                            }
                                            onChange={e => {
                                                const next = new Map(
                                                    selectedEndorsements
                                                );
                                                if (e.target.value === '') {
                                                    next.delete(type);
                                                } else {
                                                    next.set(
                                                        type,
                                                        e.target.value
                                                    );
                                                }
                                                setSelectedEndorsements(next);
                                            }}
                                            row
                                            sx={{ gap: 0 }}
                                        >
                                            <FormControlLabel
                                                value=""
                                                control={<Radio size="small" />}
                                                label="None"
                                                sx={{
                                                    '& .MuiFormControlLabel-label':
                                                        { fontSize: '0.8rem' },
                                                }}
                                            />
                                            {options.map(opt => (
                                                <FormControlLabel
                                                    key={opt.endorsement}
                                                    value={opt.endorsement}
                                                    control={
                                                        <Radio size="small" />
                                                    }
                                                    label={opt.endorsement}
                                                    sx={{
                                                        '& .MuiFormControlLabel-label':
                                                            {
                                                                fontSize:
                                                                    '0.8rem',
                                                            },
                                                    }}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </Box>
                                )
                            )}
                        </Box>
                    )}

                    {/* Cash Discount Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={applyCashDiscount}
                                onChange={e =>
                                    setApplyCashDiscount(e.target.checked)
                                }
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Apply Cash Discount (when available)
                            </Typography>
                        }
                        sx={{ mt: 1 }}
                    />

                    <Divider sx={{ my: 3 }} />

                    {/* Fetch Button & Export Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isSmallScreen ? 'column' : 'row',
                            justifyContent: 'space-between',
                            alignItems: isSmallScreen ? 'stretch' : 'center',
                            gap: isSmallScreen ? 2 : 0,
                            mt: 3,
                        }}
                    >
                        {/* Fetch Button Container (left side) */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {fetchAttempted &&
                            !isLoadingRates &&
                            !fetchError ? (
                                <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    fullWidth={isSmallScreen}
                                    sx={{
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            borderColor:
                                                theme.palette.primary.dark,
                                            backgroundColor:
                                                theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    Reset Search
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleFetchRates}
                                    disabled={isFetchDisabled}
                                    fullWidth={isSmallScreen}
                                    sx={{
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        color: theme.palette.primary
                                            .contrastText,
                                        '&:hover': {
                                            backgroundColor:
                                                theme.palette.primary.dark,
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor:
                                                theme.palette.action
                                                    .disabledBackground,
                                            color: theme.palette.action
                                                .disabled,
                                        },
                                    }}
                                >
                                    {isLoadingRates ? (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                color: theme.palette.primary
                                                    .contrastText,
                                            }}
                                        />
                                    ) : (
                                        'Fetch Rates'
                                    )}
                                </Button>
                            )}
                        </Box>

                        {/* Export Button Container (right side) */}
                        <Box sx={{ width: isSmallScreen ? '100%' : 'auto' }}>
                            <Button
                                variant="contained"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={handleOpenPdfPreview}
                                disabled={
                                    isFetchDisabled ||
                                    displayedRates.length === 0
                                }
                                fullWidth={isSmallScreen}
                                sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '& .MuiSvgIcon-root': {
                                        color: theme.palette.primary
                                            .contrastText,
                                    },
                                    '&:hover': {
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor:
                                            theme.palette.action
                                                .disabledBackground,
                                        color: theme.palette.action.disabled,
                                        '& .MuiSvgIcon-root': {
                                            color: theme.palette.action
                                                .disabled,
                                        },
                                    },
                                    ml: isSmallScreen ? 0 : 2,
                                }}
                            >
                                Export PDF
                            </Button>
                        </Box>
                        {/* Save / Update Quote */}
                        <Box sx={{ width: isSmallScreen ? '100%' : 'auto' }}>
                            {isEditMode ? (
                                <Button
                                    variant="contained"
                                    startIcon={
                                        saveQuoteSaving ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <SaveIcon />
                                        )
                                    }
                                    onClick={handleUpdateQuote}
                                    disabled={saveQuoteSaving}
                                    fullWidth={isSmallScreen}
                                    sx={{
                                        ml: isSmallScreen ? 0 : 2,
                                    }}
                                >
                                    {saveQuoteSaving
                                        ? 'Updating...'
                                        : 'Update Quote'}
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<SaveIcon />}
                                    onClick={handleOpenSaveQuoteDialog}
                                    fullWidth={isSmallScreen}
                                    sx={{
                                        ml: isSmallScreen ? 0 : 2,
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    Save as Quote
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Save as Quote dialog */}
            <Dialog
                open={saveQuoteDialogOpen}
                onClose={handleCloseSaveQuoteDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save as Quote</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Link this rate lookup to an account. The quote will
                            appear under the account and in your Quotes list.
                        </Typography>
                        <FormControl fullWidth size="small" required>
                            <InputLabel shrink>Account</InputLabel>
                            <Select
                                value={saveQuoteAccountId ?? ''}
                                label="Account"
                                notched
                                onChange={e => {
                                    const val = e.target.value;
                                    if (val === '__create__') return;
                                    const id = val as number;
                                    setSaveQuoteAccountId(id || null);
                                    const allAccounts = [
                                        ...accountOptions,
                                        ...extraAccounts,
                                    ];
                                    const acc = allAccounts.find(
                                        (a: Account) => a.id === id
                                    );
                                    setSaveQuoteAccountName(acc?.name ?? '');
                                    setSaveQuoteContactId(null);
                                    setSaveQuoteContactName('');
                                }}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Select account...</em>
                                </MenuItem>
                                <MenuItem
                                    value="__create__"
                                    onClick={() => {
                                        setCreateAccountFromSaveQuote(true);
                                        setCreateAccountName('');
                                        setCreateAccountDialogOpen(true);
                                    }}
                                    sx={{ color: 'primary.main' }}
                                >
                                    <AddIcon fontSize="small" sx={{ mr: 1 }} />
                                    New Account
                                </MenuItem>
                                <Divider />
                                {[...accountOptions, ...extraAccounts].map(
                                    (acc: Account) => (
                                        <MenuItem key={acc.id} value={acc.id}>
                                            {acc.name}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                        <FormControl
                            fullWidth
                            size="small"
                            disabled={!saveQuoteAccountId}
                        >
                            <InputLabel shrink>Contact (optional)</InputLabel>
                            <Select
                                value={saveQuoteContactId ?? ''}
                                label="Contact (optional)"
                                notched
                                onChange={e => {
                                    const val = e.target.value;
                                    if (val === '__create_contact__') return;
                                    const id = val as number;
                                    setSaveQuoteContactId(id || null);
                                    const c = [
                                        ...contactOptions,
                                        ...extraContacts,
                                    ].find((c: Contact) => c.id === id);
                                    setSaveQuoteContactName(
                                        c
                                            ? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
                                            : ''
                                    );
                                }}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                <MenuItem
                                    value="__create_contact__"
                                    onClick={() => {
                                        setCreateContactFirstName('');
                                        setCreateContactLastName('');
                                        setCreateContactDialogOpen(true);
                                    }}
                                    sx={{ color: 'primary.main' }}
                                >
                                    <AddIcon fontSize="small" sx={{ mr: 1 }} />
                                    New Contact
                                </MenuItem>
                                <Divider />
                                {[...contactOptions, ...extraContacts].map(
                                    (c: Contact) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {[c.first_name, c.last_name]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSaveQuoteDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveAsQuote}
                        disabled={!saveQuoteAccountId || saveQuoteSaving}
                        startIcon={
                            saveQuoteSaving ? (
                                <CircularProgress size={20} />
                            ) : (
                                <SaveIcon />
                            )
                        }
                    >
                        {saveQuoteSaving ? 'Saving...' : 'Save Quote'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Account dialog */}
            <Dialog
                open={createAccountDialogOpen}
                onClose={() => {
                    if (!createAccountSaving) {
                        setCreateAccountDialogOpen(false);
                        setCreateAccountFromSaveQuote(false);
                    }
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>New account</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Account name"
                        fullWidth
                        variant="standard"
                        value={createAccountName}
                        onChange={e => setCreateAccountName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateAccountFromDialog();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setCreateAccountDialogOpen(false);
                            setCreateAccountFromSaveQuote(false);
                        }}
                        disabled={createAccountSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateAccountFromDialog}
                        disabled={
                            !createAccountName.trim() || createAccountSaving
                        }
                        startIcon={
                            createAccountSaving ? (
                                <CircularProgress size={20} />
                            ) : (
                                <AddIcon />
                            )
                        }
                    >
                        {createAccountSaving ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Contact dialog (from Save as Quote) */}
            <Dialog
                open={createContactDialogOpen}
                onClose={() => {
                    if (!createContactSaving) {
                        setCreateContactDialogOpen(false);
                    }
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>New contact</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            autoFocus
                            label="First name"
                            fullWidth
                            size="small"
                            value={createContactFirstName}
                            onChange={e =>
                                setCreateContactFirstName(e.target.value)
                            }
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateContactFromDialog();
                                }
                            }}
                        />
                        <TextField
                            label="Last name"
                            fullWidth
                            size="small"
                            value={createContactLastName}
                            onChange={e =>
                                setCreateContactLastName(e.target.value)
                            }
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateContactFromDialog();
                                }
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateContactDialogOpen(false)}
                        disabled={createContactSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateContactFromDialog}
                        disabled={
                            !createContactFirstName.trim() ||
                            !createContactLastName.trim() ||
                            createContactSaving
                        }
                        startIcon={
                            createContactSaving ? (
                                <CircularProgress size={20} />
                            ) : (
                                <AddIcon />
                            )
                        }
                    >
                        {createContactSaving ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Results Section */}
            <Box sx={{ mt: 4 }}>
                {fetchError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {fetchError}
                    </Alert>
                )}
                {isLoadingRates && !fetchError && (
                    <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {!isLoadingRates &&
                    !fetchError &&
                    (ratesByLine.length > 0 || tableData.length > 0) && (
                        <>
                            {/* Per-line rate tables when multiple location/crop lines */}
                            {ratesByLine.length > 0 &&
                                tableDataByLine.map(
                                    ({
                                        label,
                                        entry,
                                        tableData: lineTableData,
                                    }) => (
                                        <Paper
                                            key={entry?.id ?? label}
                                            elevation={1}
                                            sx={{
                                                p: 0,
                                                position: 'relative',
                                                mb: 2,
                                            }}
                                        >
                                            {isUpdatingDisplay && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor:
                                                            'rgba(255, 255, 255, 0.7)',
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                        alignItems: 'center',
                                                        zIndex: 10,
                                                    }}
                                                >
                                                    <CircularProgress />
                                                </Box>
                                            )}
                                            <Typography
                                                variant="h6"
                                                sx={{ p: 2, pb: 1 }}
                                            >
                                                {label}
                                            </Typography>
                                            <TableContainer
                                                sx={{
                                                    overflowX: 'auto',
                                                    '&::-webkit-scrollbar': {
                                                        height: 8,
                                                    },
                                                    '&::-webkit-scrollbar-track':
                                                        {
                                                            backgroundColor:
                                                                '#f1f1f1',
                                                        },
                                                    '&::-webkit-scrollbar-thumb':
                                                        {
                                                            backgroundColor:
                                                                '#888',
                                                            borderRadius: 4,
                                                        },
                                                }}
                                            >
                                                <Table
                                                    size="small"
                                                    sx={{
                                                        minWidth: isSmallScreen
                                                            ? 400
                                                            : 'auto',
                                                        '& .MuiTableCell-root':
                                                            {
                                                                whiteSpace:
                                                                    'nowrap',
                                                                px: isSmallScreen
                                                                    ? 1
                                                                    : 2,
                                                            },
                                                        '& .carrier-column': {
                                                            position:
                                                                isSmallScreen
                                                                    ? 'sticky'
                                                                    : 'static',
                                                            left: 0,
                                                            backgroundColor:
                                                                'white',
                                                            zIndex: 1,
                                                            borderRight:
                                                                isSmallScreen
                                                                    ? '1px solid rgba(224, 224, 224, 1)'
                                                                    : 'none',
                                                            minWidth:
                                                                isSmallScreen
                                                                    ? 100
                                                                    : 'auto',
                                                            maxWidth:
                                                                isSmallScreen
                                                                    ? 120
                                                                    : 'none',
                                                        },
                                                    }}
                                                >
                                                    <TableHead
                                                        sx={{
                                                            bgcolor: 'grey.100',
                                                        }}
                                                    >
                                                        <TableRow>
                                                            <TableCell
                                                                className="carrier-column"
                                                                rowSpan={2}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 0.5,
                                                                    }}
                                                                >
                                                                    Carrier
                                                                    {applyCashDiscount && (
                                                                        <Chip
                                                                            label="Discount | Due"
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{
                                                                                fontSize:
                                                                                    '0.6rem',
                                                                                height: 18,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            {formData.selectedHailProducts?.map(
                                                                product => (
                                                                    <TableCell
                                                                        key={
                                                                            product
                                                                        }
                                                                        colSpan={
                                                                            (applyCashDiscount
                                                                                ? 3
                                                                                : 2) +
                                                                            activeEndorsementCount
                                                                        }
                                                                        align="center"
                                                                        sx={{
                                                                            minWidth:
                                                                                isSmallScreen
                                                                                    ? 100
                                                                                    : 'auto',
                                                                            borderRight:
                                                                                '1px solid rgba(224, 224, 224, 1)',
                                                                        }}
                                                                    >
                                                                        {
                                                                            product
                                                                        }
                                                                    </TableCell>
                                                                )
                                                            )}
                                                        </TableRow>
                                                        <TableRow>
                                                            {formData.selectedHailProducts?.map(
                                                                product => (
                                                                    <React.Fragment
                                                                        key={
                                                                            product
                                                                        }
                                                                    >
                                                                        <TableCell
                                                                            align="right"
                                                                            sx={{
                                                                                minWidth:
                                                                                    isSmallScreen
                                                                                        ? 60
                                                                                        : 70,
                                                                                fontSize:
                                                                                    '0.75rem',
                                                                            }}
                                                                        >
                                                                            Rate
                                                                        </TableCell>
                                                                        {Array.from(
                                                                            selectedEndorsements.entries()
                                                                        ).map(
                                                                            ([
                                                                                type,
                                                                                name,
                                                                            ]) => (
                                                                                <TableCell
                                                                                    key={
                                                                                        type
                                                                                    }
                                                                                    align="right"
                                                                                    sx={{
                                                                                        minWidth:
                                                                                            isSmallScreen
                                                                                                ? 60
                                                                                                : 70,
                                                                                        fontSize:
                                                                                            '0.75rem',
                                                                                    }}
                                                                                >
                                                                                    {name.replace(
                                                                                        'Wind Shatter ',
                                                                                        'WSH '
                                                                                    )}
                                                                                </TableCell>
                                                                            )
                                                                        )}
                                                                        {applyCashDiscount && (
                                                                            <TableCell
                                                                                align="right"
                                                                                sx={{
                                                                                    minWidth:
                                                                                        isSmallScreen
                                                                                            ? 60
                                                                                            : 70,
                                                                                    fontSize:
                                                                                        '0.75rem',
                                                                                }}
                                                                            >
                                                                                Disc.
                                                                                Rate
                                                                            </TableCell>
                                                                        )}
                                                                        <TableCell
                                                                            align="right"
                                                                            sx={{
                                                                                minWidth:
                                                                                    isSmallScreen
                                                                                        ? 70
                                                                                        : 80,
                                                                                fontSize:
                                                                                    '0.75rem',
                                                                            }}
                                                                        >
                                                                            Cost/Acre
                                                                        </TableCell>
                                                                    </React.Fragment>
                                                                )
                                                            )}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {lineTableData.map(
                                                            (row, index) => (
                                                                <TableRow
                                                                    key={
                                                                        row.carrier ||
                                                                        index
                                                                    }
                                                                    sx={{
                                                                        '&:last-child td, &:last-child th':
                                                                            {
                                                                                border: 0,
                                                                            },
                                                                    }}
                                                                >
                                                                    <TableCell className="carrier-column">
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: 0.5,
                                                                                flexWrap:
                                                                                    'wrap',
                                                                            }}
                                                                        >
                                                                            {row.carrier ===
                                                                            'proAg'
                                                                                ? 'Pro Ag'
                                                                                : row.carrier ||
                                                                                  '-'}
                                                                            {applyCashDiscount &&
                                                                                (() => {
                                                                                    const info =
                                                                                        discountChipMap.get(
                                                                                            row.carrier
                                                                                        );
                                                                                    return info ? (
                                                                                        <Chip
                                                                                            label={`${info.pct}${info.due ? ' | ' + info.due : ''}`}
                                                                                            size="small"
                                                                                            color="success"
                                                                                            variant="outlined"
                                                                                            sx={{
                                                                                                fontSize:
                                                                                                    '0.65rem',
                                                                                                height: 20,
                                                                                            }}
                                                                                        />
                                                                                    ) : null;
                                                                                })()}
                                                                        </Box>
                                                                    </TableCell>
                                                                    {formData.selectedHailProducts?.map(
                                                                        product => {
                                                                            const cell =
                                                                                row
                                                                                    .products[
                                                                                    product
                                                                                ];
                                                                            if (
                                                                                !cell
                                                                            )
                                                                                return (
                                                                                    <React.Fragment
                                                                                        key={
                                                                                            product
                                                                                        }
                                                                                    >
                                                                                        <TableCell align="right">
                                                                                            -
                                                                                        </TableCell>
                                                                                        {Array.from(
                                                                                            selectedEndorsements.entries()
                                                                                        ).map(
                                                                                            ([
                                                                                                type,
                                                                                            ]) => (
                                                                                                <TableCell
                                                                                                    key={
                                                                                                        type
                                                                                                    }
                                                                                                    align="right"
                                                                                                >
                                                                                                    -
                                                                                                </TableCell>
                                                                                            )
                                                                                        )}
                                                                                        {applyCashDiscount && (
                                                                                            <TableCell align="right">
                                                                                                -
                                                                                            </TableCell>
                                                                                        )}
                                                                                        <TableCell align="right">
                                                                                            -
                                                                                        </TableCell>
                                                                                    </React.Fragment>
                                                                                );
                                                                            return (
                                                                                <React.Fragment
                                                                                    key={
                                                                                        product
                                                                                    }
                                                                                >
                                                                                    <TableCell
                                                                                        align="right"
                                                                                        sx={{
                                                                                            minWidth:
                                                                                                isSmallScreen
                                                                                                    ? 60
                                                                                                    : 70,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            cell.rateDisplay
                                                                                        }
                                                                                    </TableCell>
                                                                                    {Array.from(
                                                                                        selectedEndorsements.entries()
                                                                                    ).map(
                                                                                        ([
                                                                                            type,
                                                                                            endorsementName,
                                                                                        ]) => {
                                                                                            const endoRate =
                                                                                                getEndorsementRate(
                                                                                                    row.carrier,
                                                                                                    entry?.crop ||
                                                                                                        '',
                                                                                                    entry?.county ||
                                                                                                        '',
                                                                                                    product,
                                                                                                    endorsementName
                                                                                                );
                                                                                            return (
                                                                                                <TableCell
                                                                                                    key={
                                                                                                        type
                                                                                                    }
                                                                                                    align="right"
                                                                                                    sx={{
                                                                                                        minWidth:
                                                                                                            isSmallScreen
                                                                                                                ? 60
                                                                                                                : 70,
                                                                                                    }}
                                                                                                >
                                                                                                    {endoRate !==
                                                                                                    null ? (
                                                                                                        endoRate.toFixed(
                                                                                                            2
                                                                                                        )
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            variant="body2"
                                                                                                            color="text.disabled"
                                                                                                        >
                                                                                                            N/A
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </TableCell>
                                                                                            );
                                                                                        }
                                                                                    )}
                                                                                    {applyCashDiscount && (
                                                                                        <TableCell
                                                                                            align="right"
                                                                                            sx={{
                                                                                                minWidth:
                                                                                                    isSmallScreen
                                                                                                        ? 60
                                                                                                        : 70,
                                                                                                color: '#1b8a6b',
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                cell.discRateDisplay
                                                                                            }
                                                                                        </TableCell>
                                                                                    )}
                                                                                    <TableCell
                                                                                        align="right"
                                                                                        sx={{
                                                                                            minWidth:
                                                                                                isSmallScreen
                                                                                                    ? 70
                                                                                                    : 80,
                                                                                            ...(applyCashDiscount && {
                                                                                                color: '#1b8a6b',
                                                                                            }),
                                                                                        }}
                                                                                    >
                                                                                        {applyCashDiscount
                                                                                            ? cell.costPerAcreDiscDisplay
                                                                                            : cell.costPerAcreDisplay}
                                                                                    </TableCell>
                                                                                </React.Fragment>
                                                                            );
                                                                        }
                                                                    )}
                                                                </TableRow>
                                                            )
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Paper>
                                    )
                                )}

                            {/* Single table when no multi-line results (legacy / single line) */}
                            {ratesByLine.length === 0 &&
                                tableData.length > 0 && (
                                    <Paper
                                        elevation={1}
                                        sx={{ p: 0, position: 'relative' }}
                                    >
                                        {isUpdatingDisplay && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor:
                                                        'rgba(255, 255, 255, 0.7)',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    zIndex: 10,
                                                }}
                                            >
                                                <CircularProgress />
                                            </Box>
                                        )}
                                        <Typography
                                            variant="h6"
                                            sx={{ p: 2, pb: 1 }}
                                        >
                                            Matching Rates
                                        </Typography>
                                        {displayedRates.length > 0 && (
                                            <Box sx={{ px: 2, pb: 2 }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                    sx={{
                                                        '& .MuiChip-root': {
                                                            mb: isSmallScreen
                                                                ? 1
                                                                : 0,
                                                        },
                                                    }}
                                                >
                                                    {displayedRates[0]
                                                        ?.crop_year && (
                                                        <Chip
                                                            label={
                                                                displayedRates[0]
                                                                    .crop_year
                                                            }
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    )}
                                                    {displayedRates[0]
                                                        ?.state && (
                                                        <Chip
                                                            label={
                                                                displayedRates[0]
                                                                    .state
                                                            }
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    )}
                                                    {displayedRates[0]
                                                        ?.county && (
                                                        <Chip
                                                            label={normalizeCase(
                                                                displayedRates[0]
                                                                    .county
                                                            )}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    )}
                                                    {formData.crop && (
                                                        <Chip
                                                            label={
                                                                formData.crop
                                                            }
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    )}
                                                    {displayedRates[0]
                                                        ?.township && (
                                                        <Chip
                                                            label={
                                                                displayedRates[0]
                                                                    .township
                                                            }
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    )}
                                                    {displayedRates[0]
                                                        ?.range && (
                                                        <Chip
                                                            label={
                                                                displayedRates[0]
                                                                    .range
                                                            }
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}
                                        <TableContainer
                                            sx={{
                                                overflowX: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    height: 8,
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: '#f1f1f1',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: '#888',
                                                    borderRadius: 4,
                                                },
                                                '&::-webkit-scrollbar-thumb:hover':
                                                    {
                                                        backgroundColor: '#555',
                                                    },
                                            }}
                                        >
                                            <Table
                                                size="small"
                                                sx={{
                                                    minWidth: isSmallScreen
                                                        ? 400
                                                        : 'auto',
                                                    '& .MuiTableCell-root': {
                                                        whiteSpace: 'nowrap',
                                                        px: isSmallScreen
                                                            ? 1
                                                            : 2,
                                                    },
                                                    '& .carrier-column': {
                                                        position: isSmallScreen
                                                            ? 'sticky'
                                                            : 'static',
                                                        left: 0,
                                                        backgroundColor:
                                                            'white',
                                                        zIndex: 1,
                                                        borderRight:
                                                            isSmallScreen
                                                                ? '1px solid rgba(224, 224, 224, 1)'
                                                                : 'none',
                                                        minWidth: isSmallScreen
                                                            ? 100
                                                            : 'auto',
                                                        maxWidth: isSmallScreen
                                                            ? 120
                                                            : 'none',
                                                    },
                                                }}
                                            >
                                                <TableHead
                                                    sx={{ bgcolor: 'grey.100' }}
                                                >
                                                    <TableRow>
                                                        <TableCell
                                                            className="carrier-column"
                                                            rowSpan={2}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                Carrier
                                                                {applyCashDiscount && (
                                                                    <Chip
                                                                        label="Discount | Due"
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontSize:
                                                                                '0.6rem',
                                                                            height: 18,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        {formData.selectedHailProducts?.map(
                                                            product => (
                                                                <TableCell
                                                                    key={
                                                                        product
                                                                    }
                                                                    colSpan={
                                                                        applyCashDiscount
                                                                            ? 3
                                                                            : 2
                                                                    }
                                                                    align="center"
                                                                    sx={{
                                                                        minWidth:
                                                                            isSmallScreen
                                                                                ? 100
                                                                                : 'auto',
                                                                        borderRight:
                                                                            '1px solid rgba(224, 224, 224, 1)',
                                                                    }}
                                                                >
                                                                    {product}
                                                                </TableCell>
                                                            )
                                                        )}
                                                    </TableRow>
                                                    <TableRow>
                                                        {formData.selectedHailProducts?.map(
                                                            product => (
                                                                <React.Fragment
                                                                    key={
                                                                        product
                                                                    }
                                                                >
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            minWidth:
                                                                                isSmallScreen
                                                                                    ? 60
                                                                                    : 70,
                                                                            fontSize:
                                                                                '0.75rem',
                                                                        }}
                                                                    >
                                                                        Rate
                                                                    </TableCell>
                                                                    {applyCashDiscount && (
                                                                        <TableCell
                                                                            align="right"
                                                                            sx={{
                                                                                minWidth:
                                                                                    isSmallScreen
                                                                                        ? 60
                                                                                        : 70,
                                                                                fontSize:
                                                                                    '0.75rem',
                                                                            }}
                                                                        >
                                                                            Disc.
                                                                            Rate
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            minWidth:
                                                                                isSmallScreen
                                                                                    ? 70
                                                                                    : 80,
                                                                            fontSize:
                                                                                '0.75rem',
                                                                        }}
                                                                    >
                                                                        Cost/Acre
                                                                    </TableCell>
                                                                </React.Fragment>
                                                            )
                                                        )}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tableData.map(
                                                        (row, index) => (
                                                            <TableRow
                                                                key={
                                                                    row.carrier ||
                                                                    index
                                                                }
                                                                sx={{
                                                                    '&:last-child td, &:last-child th':
                                                                        {
                                                                            border: 0,
                                                                        },
                                                                }}
                                                            >
                                                                <TableCell className="carrier-column">
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 0.5,
                                                                            flexWrap:
                                                                                'wrap',
                                                                        }}
                                                                    >
                                                                        {row.carrier ===
                                                                        'proAg'
                                                                            ? 'Pro Ag'
                                                                            : row.carrier ||
                                                                              '-'}
                                                                        {applyCashDiscount &&
                                                                            (() => {
                                                                                const info =
                                                                                    discountChipMap.get(
                                                                                        row.carrier
                                                                                    );
                                                                                return info ? (
                                                                                    <Chip
                                                                                        label={`${info.pct}${info.due ? ' | ' + info.due : ''}`}
                                                                                        size="small"
                                                                                        color="success"
                                                                                        variant="outlined"
                                                                                        sx={{
                                                                                            fontSize:
                                                                                                '0.65rem',
                                                                                            height: 20,
                                                                                        }}
                                                                                    />
                                                                                ) : null;
                                                                            })()}
                                                                    </Box>
                                                                </TableCell>
                                                                {formData.selectedHailProducts?.map(
                                                                    product => {
                                                                        const cell =
                                                                            row
                                                                                .products[
                                                                                product
                                                                            ];
                                                                        if (
                                                                            !cell
                                                                        )
                                                                            return (
                                                                                <React.Fragment
                                                                                    key={
                                                                                        product
                                                                                    }
                                                                                >
                                                                                    <TableCell align="right">
                                                                                        -
                                                                                    </TableCell>
                                                                                    {applyCashDiscount && (
                                                                                        <TableCell align="right">
                                                                                            -
                                                                                        </TableCell>
                                                                                    )}
                                                                                    <TableCell align="right">
                                                                                        -
                                                                                    </TableCell>
                                                                                </React.Fragment>
                                                                            );
                                                                        return (
                                                                            <React.Fragment
                                                                                key={
                                                                                    product
                                                                                }
                                                                            >
                                                                                <TableCell
                                                                                    align="right"
                                                                                    sx={{
                                                                                        minWidth:
                                                                                            isSmallScreen
                                                                                                ? 60
                                                                                                : 70,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        cell.rateDisplay
                                                                                    }
                                                                                </TableCell>
                                                                                {applyCashDiscount && (
                                                                                    <TableCell
                                                                                        align="right"
                                                                                        sx={{
                                                                                            minWidth:
                                                                                                isSmallScreen
                                                                                                    ? 60
                                                                                                    : 70,
                                                                                            color: '#1b8a6b',
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            cell.discRateDisplay
                                                                                        }
                                                                                    </TableCell>
                                                                                )}
                                                                                <TableCell
                                                                                    align="right"
                                                                                    sx={{
                                                                                        minWidth:
                                                                                            isSmallScreen
                                                                                                ? 70
                                                                                                : 80,
                                                                                        ...(applyCashDiscount && {
                                                                                            color: '#1b8a6b',
                                                                                        }),
                                                                                    }}
                                                                                >
                                                                                    {applyCashDiscount
                                                                                        ? cell.costPerAcreDiscDisplay
                                                                                        : cell.costPerAcreDisplay}
                                                                                </TableCell>
                                                                            </React.Fragment>
                                                                        );
                                                                    }
                                                                )}
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                )}
                        </>
                    )}
                {/* Updated message condition: show if fetch attempted, not loading, no error, and displayedRates is empty */}
                {fetchAttempted &&
                    !isLoadingRates &&
                    !fetchError &&
                    displayedRates.length === 0 &&
                    processedRates.length > 0 &&
                    formData.selectedHailProducts &&
                    formData.selectedHailProducts.length > 0 && (
                        <Typography
                            sx={{ mt: 2, fontStyle: 'italic' }}
                        >{`No rates found matching the currently selected Hail Products for this location/class.`}</Typography>
                    )}
                {/* Message for when the initial fetch itself found nothing applicable */}
                {fetchAttempted &&
                    !isLoadingRates &&
                    !fetchError &&
                    processedRates.length === 0 &&
                    formData.state &&
                    formData.county &&
                    formData.township &&
                    formData.range &&
                    formData.crop &&
                    formData.selectedCarriers &&
                    formData.selectedCarriers.length > 0 && (
                        <Typography
                            sx={{ mt: 2, fontStyle: 'italic' }}
                        >{`No rates found for the specified location and crop with the selected carriers.`}</Typography>
                    )}
            </Box>

            {/* PDF Preview Modal */}
            <Dialog
                open={pdfPreviewOpen}
                onClose={handleClosePdfPreview}
                fullWidth
                maxWidth="lg" // Use a large width for better preview
                aria-labelledby="pdf-preview-dialog-title"
            >
                <DialogTitle id="pdf-preview-dialog-title">
                    PDF Preview
                    <IconButton
                        aria-label="close"
                        onClick={handleClosePdfPreview}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ height: '70vh', p: 0 }}>
                    <BlobProvider
                        document={
                            <RatePdfDocument
                                formData={formData}
                                insuredName={formData.insuredName}
                                accountName={selectedFormAccount?.name}
                                ipa={
                                    formData.ipa ??
                                    locationCropLines[0]?.ipa ??
                                    1
                                }
                                rates={displayedRates}
                                lines={pdfLines}
                                applicableHailProducts={
                                    formData.selectedHailProducts || []
                                }
                                identity={identity}
                                cropWestLogoUrl={cropWestLogoUrl}
                                discountInfo={pdfDiscountInfo}
                                selectedEndorsements={pdfSelectedEndorsements}
                                endorsementRateLookup={pdfEndorsementRateLookup}
                            />
                        }
                    >
                        {({ url, loading, error }) => {
                            if (loading) {
                                return (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <CircularProgress />
                                        <Typography sx={{ ml: 2 }}>
                                            Generating preview...
                                        </Typography>
                                    </Box>
                                );
                            }
                            if (error) {
                                return (
                                    <Typography color="error" sx={{ p: 2 }}>
                                        Error generating PDF preview.
                                    </Typography>
                                );
                            }
                            return <PdfPreviewRenderer url={url} />;
                        }}
                    </BlobProvider>
                </DialogContent>
                <DialogActions
                    sx={{ justifyContent: 'space-between', px: 2, py: 1 }}
                >
                    {/* Show PDFDownloadLink only on client-side */}
                    {typeof window !== 'undefined' ? (
                        <PDFDownloadLink
                            document={
                                <RatePdfDocument
                                    formData={formData}
                                    insuredName={formData.insuredName}
                                    accountName={selectedFormAccount?.name}
                                    ipa={
                                        formData.ipa ??
                                        locationCropLines[0]?.ipa ??
                                        1
                                    }
                                    rates={displayedRates}
                                    lines={pdfLines}
                                    applicableHailProducts={
                                        formData.selectedHailProducts || []
                                    }
                                    identity={identity}
                                    cropWestLogoUrl={cropWestLogoUrl}
                                    discountInfo={pdfDiscountInfo}
                                    selectedEndorsements={
                                        pdfSelectedEndorsements
                                    }
                                    endorsementRateLookup={
                                        pdfEndorsementRateLookup
                                    }
                                />
                            }
                            fileName={`HailRates_${formData.state || 'NA'}_${formData.county || 'NA'}_${new Date().toISOString().slice(0, 10)}.pdf`}
                            style={{ textDecoration: 'none' }} // Remove underline from link
                        >
                            {({ blob, url, loading, error }) => (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                    startIcon={<PictureAsPdfIcon />}
                                >
                                    {loading ? 'Generating...' : 'Download PDF'}
                                </Button>
                            )}
                        </PDFDownloadLink>
                    ) : (
                        <Button variant="contained" disabled>
                            Download PDF
                        </Button> // Placeholder for SSR
                    )}
                    <Button variant="outlined" onClick={handleClosePdfPreview}>
                        Close Preview
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Remove or update path if necessary - this might conflict if not the intended route
// HailPricingToolPage.path = '/hail-pricing-tool/rate-manager';
